import Person from "../../model/Person";
import {
    formatPercentile,
    getDateToday, getISODateFromDate,
    getISODateTimeFromDate,
    getISOYear,
    isoToLocalDate,
    numberToPercentFormat,
    roundMoney
} from "../../stores/StoreUtilities";
import {AssetIncomeDetail, Gender, MaritalStatus} from "../../API";
import {addMonths, addYears, compareAsc, differenceInYears, endOfYear, getDayOfYear, getDaysInYear} from "date-fns";
import Income, {IncomeType} from "../../model/Income";
import Expense, {ExpenseCategory} from "../../model/Expense";
import Deduction from "../../model/Deduction";
import Tax, {TaxType} from "../../model/Tax";
import Scheduled from "../../model/Scheduled";
import Snapshot, {ISnapshotDetail} from "../../model/Snapshot";
import Liability from "../../model/Liability";
import Asset, {AssetCategory, AssetType} from "../../model/Asset";
import Plan from "../../model/Plan";
import GrowthStrategy, {GrowthModel} from "../../model/GrowthStrategy";
import Model, {ModelSetting} from "../../model/Model";
import TaxValue, {TaxValueKey} from "../../model/TaxValue";
import {TaxEstimate, TaxEstimator, TaxFilingStatus, TaxInput} from "../tax/TaxEstimator";
import {Schedule as RSchedule} from "../../components/rschedule";
import {RuleOption} from "@rschedule/core";
import {Frequency} from "../../model/Schedule";
import AssetConversion, {AssetConversionType} from "../../model/AssetConversion";
import {isNumber} from "../../model/ModelUtilities";
import {IInflationStrategy} from "../../model/changes/InflationStrategyChange";
import StatCalc from "./StatCalc";
import RateModels, {RateModel, ReturnSequence} from "./RateModels";
import range from "lodash.range";
import clone from "lodash.clone";
import IModelItem from "../../model/IModelItem";
import {stateNumberToCode} from "../form/StateSelector";

export interface IPersonTimeline {
    birthDate: Date
    retireDate: Date
    iraWithdrawalDate: Date
    earlySocialSecurityDate: Date
    medicareDate: Date
    fullSocialSecurityDate: Date
    delayedSocialSecurityDate: Date
    qualifiedCharitableDistributionDate: Date
    nonInheritedRMDDate: Date
    lifeExpectancyDate: Date

    birthYear: number
    age: number
    retireAge: number
    earlySocialSecurityAge: number
    fullSocialSecurityAge: number
    delayedSocialSecurityAge: number
    yearsBeforeRetirement: number
    yearsToRetirement: number
    yearsInRetirement: number
    yearsRemainingInRetirement: number
    lifeExpectancyAge: number
}

export interface INetWorthCalc {
    id: string
    date: Date
    liquidAssetsTotal: number
    realAssetsTotal: number
    personalAssetsTotal: number
    otherAssetsTotal: number
    assetsTotal: number
    excludedAssetsTotal: number
    liabilitiesTotal: number
    netWorthTotal: number
}

export interface IBudgetItemDetail {
    date: Date
    id: string
    itemType: string // 'Income' | 'Deduction' | 'Tax' | 'Expense'
    discretionary?: boolean
    description: string
    amount: number
}

export interface IIncomeItemSummary {
    id: string
    description: string
    incomeType: IncomeType
    total: number
    excludeFromNIIT?: boolean
}

export interface IIncomeSummary {
    year: number
    total: number
    byItem: IIncomeItemSummary[]
    byType: IIncomeTypeSummary[]
    byMonth: number[]
}

export interface IIncomeTypeSummary {
    incomeType: IncomeType
    total: number
}

export interface IDeductionItemSummary {
    id: string
    description: string
    total: number
}

export interface IDeductionSummary {
    year: number
    total: number
    byItem: IDeductionItemSummary[]
    byMonth: number[]
}

export interface ITaxItemSummary {
    id: string
    description: string
    total: number
}

export interface ITaxSummary {
    year: number
    total: number
    byItem: ITaxItemSummary[]
    byType: ITaxTypeSummary[]
    byMonth: number[]
}

export interface ITaxTypeSummary {
    taxType: TaxType
    total: number
}

export interface IExpenseItemSummary {
    id: string
    description: string
    total: number
}

export interface IExpenseCategorySummary {
    category: ExpenseCategory
    total: number
    essential: number
    discretionary: number
    byItem: IExpenseItemSummary[]
    byMonth: number[]
}

export interface IExpenseWithdrawal {
    assetId: string,
    amount: number
}

export interface IExpenseSummary {
    year: number
    total: number
    essential: number
    discretionary: number
    byCategory: IExpenseCategorySummary[]
    byMonth: number[]
    byMonthEssential: number[]
    byMonthDiscretionary: number[]
    withdrawals: IExpenseWithdrawal[]
}

export interface IBudgetCalc {
    year: number
    date: Date
    incomeSummary: IIncomeSummary
    deductionSummary: IDeductionSummary
    taxSummary: ITaxSummary
    expenseSummary: IExpenseSummary
    details: IBudgetItemDetail[]
    totalIncome: number
    totalGuaranteedIncome: number
    totalDeductions: number
    totalTaxes: number
    totalEstimatedTaxes: number
    totalEssentialExpenses: number
    totalDiscretionaryExpenses: number
    totalExpenses: number
    netIncome: number
    model: Model
}

export class LiquidAsset extends Asset {
    ownerAge: number
    spouseAge: number
    rmdBeginAge: number
    distYearsLeft: number
    distPeriodStart: number
    order: number

    constructor (data: any) {
        super(data)
        this.ownerAge = 0
        this.spouseAge = 0
        this.rmdBeginAge = 0
        this.distYearsLeft = 0
        this.distPeriodStart = 0
        this.order = this.withdrawalOrder
    }
}

export class BenchmarkData{
    latestRetirementYear: number
    startYear: number
    endYear: number
    guaranteedIncome: number
    essentialExpenses: number
    withdrawalRate: number

    constructor () {
        this.latestRetirementYear = 0
        this.startYear = 0
        this.endYear = 0
        this.guaranteedIncome = 0
        this.essentialExpenses = 0
        this.withdrawalRate = 0
    }

    get secureIncomeScore() {
        return (this.guaranteedIncome / this.essentialExpenses)
    }
}

export class FutureCalc {
    label: string
    key: string
    person1?: Person
    person2?: Person
    person1RmdData?: any // TODO
    person2RmdData?: any // TODO
    maxAge: number
    latestRetirementYear: number
    latestLifeExpectancyYear: number
    incomes: Income[]
    activeIncomes: Income[]
    deductions: Deduction[]
    taxes: Tax[]
    liquidAssets: LiquidAsset[]
    realAssets: Asset[]
    snapshots: Snapshot[]
    iraAssets: LiquidAsset[]
    taxableIraAssets: LiquidAsset[]
    nonQualifiedAssets: LiquidAsset[]
    liabilities: Liability[]
    model?: Model
    plan?: Plan
    originalLiquidAssetTotal: number
    originalLiabilityTotal: number
    growthStrategy: GrowthStrategy
    inflationStrategy: IInflationStrategy
    startYear: number
    endYear: number
    futureYears: IFutureYear[]
    benchmarkData: BenchmarkData

    totalTax: number
    netLiquidAssets: number
    netRealAssets: number
    netLiabilities: number

    constructor() {
        this.label = ""
        this.key = ""
        this.latestRetirementYear = 0
        this.latestLifeExpectancyYear = 0
        this.maxAge = 0
        this.incomes = []
        this.activeIncomes = []
        this.deductions = []
        this.taxes = []
        this.liquidAssets = []
        this.realAssets = []
        this.snapshots = []
        this.iraAssets = []
        this.taxableIraAssets = []
        this.nonQualifiedAssets = []
        this.liabilities = []
        this.originalLiquidAssetTotal = 0
        this.originalLiabilityTotal = 0
        this.growthStrategy = new GrowthStrategy({})
        this.inflationStrategy = {
            deduction: {rate: 0.03, lock: false},
            expense: {rate: 0.03, lock: false},
            income: {rate: 0.03, lock: false},
            tax: {rate: 0.03, lock: false}
        }
        this.startYear = 0
        this.endYear = 0
        this.futureYears = []
        this.benchmarkData = new BenchmarkData()

        this.totalTax = 0
        this.netLiquidAssets = 0
        this.netRealAssets = 0
        this.netLiabilities = 0
    }
}

export interface IFutureYear {
    yearNum: number
    year: number
    startAt: Date
    person1Age: number
    person2Age: number
    expectedMaritalStatus: MaritalStatus
    growthRate: number
    grossIncome: number
    remainingIncome: number
    guaranteedIncome: number
    deductions: number,
    remainingDeductions: number,
    taxes: number,
    remainingTaxes: number,
    taxRate: number,
    netIncome: number,
    allExpenses: number,
    essentialExpenses: number,
    expenseDistributions: number,
    remainingDistributions: number,
    discretionaryExpenses: number,
    remainingExpenses: number
    rmds: number
    remainingRMDs: number
    contributions: number
    surplus: number // surplus or shortfall
    remainingSurplus: number,
    iraDistributions: number
    taxableIraDistributions: number
    unqualifiedDistributions: number
    qualifiedCapitalGainsDistributions: number
    allDistributions: number
    currentLiquidAssets: number
    netLiquidAssets: number
    currentRealAssets: number
    netRealAssets: number
    currentLiabilities: number
    netLiabilities: number
    yoyChange: number
    withdrawalRate: number
    incomeDetails: IIncomeDetail[]
    expenseDetails: IExpenseDetail[]
    expenseCategorySummary: IExpenseCategorySummary[] // Deprecated
    assetDetails: IAssetDetail[]
    realDetails: IRealAssetDetail[]
    liabilityDetails: ILiabilityDetail[]
    budgetCalc: IBudgetCalc
    proRateBudget?: IBudgetCalc // For first year
    taxInput?: TaxInput
    taxEstimate?: TaxEstimate
    conversions: AssetConversion[]
    unaccounted: number
}

export interface IIncomeDetail {
    id: string,
    description: string,
    incomeType: IncomeType,
    amount: number
    remaining: number
}

export interface IExpenseDetail {
    category: ExpenseCategory,
    // description: string,
    amount: number,
    remaining?: number
}

export interface IAssetDetail {
    liquidAsset: LiquidAsset
    boyBalance: number      // Beginning of Year
    ytdBalance: number      // YTD Balance
    contribution: number
    conversion: number
    rmd: number
    remainingRMD: number
    withdrawals: number
    distribution: number
    balance: number
    proportion: number
    incomeRate: number
    growthRate: number
    eoyBalance: number      // End of Year
    withdrawalRate: number
    yoyChange: number
    boyBasis: number
    eoyBasis: number
}

export interface IRealAssetDetail {
    realAsset: Asset
    boyBalance: number      // Beginning of Year
    conversion: number
    balance: number
    growthRate: number
    eoyBalance: number      // End of Year
    yoyChange: number
}

export interface ILiabilityDetail {
    liability: Liability
    interestRate: number
    expenseId?: string
    payments: number
    boyBalance: number
    eoyBalance: number
}

export interface IAssetDetailGroup {
    withdrawalOrder: number
    balance: number
    details: IAssetDetail[]
}

export interface IRateModel {
    label: string
    percentile: number
    rates: number[]
}

export interface IMonteCarloYear {
    year: number
    age1: number
    age2: number
    balances: number[]
}

export interface IMonteCarloResults {
    updatedAt: string
    rateModel: RateModel
    simulations: number
    labels: string[]
    percentiles: number[]
    years: IMonteCarloYear[]
    confidenceScore: number
    scenarioPercentile: number
}

class Calculator {
    taxEstimator: TaxEstimator
    lastFutureCalc?: FutureCalc
    rateModels = new RateModels()
    budgetCache: Map<string, IBudgetCalc> | undefined
    occurencesCache: Map<string, any[]> | undefined

    constructor(options: any) {
        this.taxEstimator = (options && options.taxEstimator) ? options.taxEstimator : null
        this.currentYear = getDateToday().getFullYear()
    }

    defaultInf: number = 0.03
    currentYear: number = 0

    lifeExpectancyYears(ageInYears: number, herAdjust: number, isMale: boolean) {
        // 2019 Update from https://www.ssa.gov/oact/STATS/table4c6.html
        var male = [76.22, 75.69, 74.72, 73.74, 72.76, 71.77, 70.78, 69.79, 68.79, 67.8, 66.81, 65.82, 64.82, 63.83, 62.85, 61.87, 60.89, 59.93, 58.97, 58.01, 57.07, 56.13, 55.2, 54.27, 53.35, 52.42, 51.5, 50.58, 49.66, 48.74, 47.83, 46.91, 46, 45.09, 44.18, 43.27, 42.36, 41.45, 40.55, 39.64, 38.74, 37.84, 36.94, 36.04, 35.15, 34.26, 33.37, 32.49, 31.61, 30.74, 29.88, 29.02, 28.18, 27.34, 26.51, 25.69, 24.89, 24.09, 23.31, 22.53, 21.77, 21.01, 20.27, 19.54, 18.81, 18.09, 17.37, 16.67, 15.97, 15.28, 14.59, 13.91, 13.25, 12.59, 11.95, 11.32, 10.71, 10.11, 9.54, 8.97, 8.43, 7.9, 7.39, 6.91, 6.44, 6, 5.57, 5.17, 4.8, 4.45, 4.12, 3.82, 3.54, 3.29, 3.06, 2.86, 2.69, 2.54, 2.4, 2.28, 2.16, 2.05, 1.94, 1.83, 1.73, 1.63, 1.54, 1.45, 1.37, 1.28, 1.21, 1.13, 1.06, 0.99, 0.92, 0.86, 0.8, 0.74, 0.68, 0.63]
        var female = [81.28, 80.69, 79.72, 78.73, 77.75, 76.76, 75.77, 74.77, 73.78, 72.79, 71.8, 70.8, 69.81, 68.82, 67.83, 66.84, 65.85, 64.87, 63.89, 62.91, 61.93, 60.95, 59.98, 59.01, 58.04, 57.07, 56.11, 55.14, 54.17, 53.21, 52.25, 51.29, 50.34, 49.38, 48.43, 47.48, 46.53, 45.59, 44.64, 43.7, 42.76, 41.82, 40.88, 39.95, 39.01, 38.08, 37.16, 36.24, 35.32, 34.41, 33.5, 32.6, 31.71, 30.82, 29.93, 29.06, 28.19, 27.33, 26.48, 25.63, 24.79, 23.96, 23.14, 22.32, 21.51, 20.7, 19.89, 19.1, 18.31, 17.52, 16.75, 16, 15.25, 14.52, 13.8, 13.1, 12.41, 11.74, 11.08, 10.45, 9.83, 9.23, 8.65, 8.09, 7.56, 7.05, 6.56, 6.1, 5.67, 5.26, 4.88, 4.52, 4.2, 3.9, 3.63, 3.39, 3.17, 2.98, 2.81, 2.65, 2.49, 2.34, 2.2, 2.07, 1.94, 1.82, 1.7, 1.59, 1.48, 1.38, 1.28, 1.19, 1.1, 1.02, 0.94, 0.87, 0.8, 0.74, 0.68, 0.63];
        var lifeExpYears = 0;
        if (herAdjust <= 0) {
            if (ageInYears >= 0 && ageInYears < 120) {
                if (isMale) {
                    lifeExpYears = male[ageInYears];
                } else {
                    lifeExpYears = female[ageInYears];
                }
            }
        } else {
            lifeExpYears = herAdjust - ageInYears;
            if (lifeExpYears < 0) {
                lifeExpYears = 0;
            }
        }
        return lifeExpYears;
    }

    personTimeline(person: Person, bDate?: Date): IPersonTimeline | null {

        if (person.birthDate || bDate) {
            const today = new Date()
            const birthDate = bDate !== undefined ? bDate : isoToLocalDate(person.birthDate)
            const birthYear = birthDate.getFullYear()

            let fullSocialSecurityAge
            if (birthYear <= 1954) {
                fullSocialSecurityAge = 66
            } else if (birthYear >= 1960) {
                fullSocialSecurityAge = 67
            } else {
                fullSocialSecurityAge = 66 + (birthYear - 1954) / 6
            }

            const retireDate = person.retireDate ? isoToLocalDate(person.retireDate) : addYears(birthDate, fullSocialSecurityAge)

            const age = bDate !== undefined ? Math.floor(differenceInYears(today, bDate)) : person.getAge()
            const lifeExpectancyAge = Math.round(this.lifeExpectancyYears(age, person.hereditaryAdjust, person.gender === Gender.Male) + age)
            let yearsBeforeRetirement = 0
            let yearsToRetirement = 0
            let yearsInRetirement = 0
            let yearsRemainingInRetirement = 0

            if (compareAsc(retireDate, today) > 0) {
                // Before retirement
                yearsBeforeRetirement = age
                yearsToRetirement = differenceInYears(retireDate, today)
                yearsRemainingInRetirement = lifeExpectancyAge - age - yearsToRetirement
            } else {
                // After retirement
                yearsBeforeRetirement = differenceInYears(retireDate, birthDate)
                yearsInRetirement = differenceInYears(today, retireDate)
                yearsRemainingInRetirement = lifeExpectancyAge - yearsBeforeRetirement - yearsInRetirement
            }
            const retireAge = differenceInYears(birthDate, retireDate)
            const iraWithdrawalDate = addMonths(birthDate, 59.5 * 12)
            const earlySocialSecurityAge = 62
            const earlySocialSecurityDate = addYears(birthDate, earlySocialSecurityAge)
            const medicareDate = addYears(birthDate, 65)

            const fullSocialSecurityDate = addMonths(birthDate, fullSocialSecurityAge * 12)
            const delayedSocialSecurityAge = 70
            const delayedSocialSecurityDate = addYears(birthDate, delayedSocialSecurityAge)
            const qualifiedCharitableDistributionDate = addMonths(birthDate, 70.5 * 12)

            let rmd_age = 75;
            if (birthYear < 1951) {
                rmd_age = 72;
            } else if (birthYear < 1960) {
                rmd_age = 73;
            } // SECURE ACT 2.0
            const nonInheritedRMDDate = addYears(birthDate, rmd_age)
            const lifeExpectancyDate = endOfYear(addYears(birthDate, lifeExpectancyAge))

            return ({
                birthDate,
                birthYear,
                retireDate,
                age,
                lifeExpectancyAge,
                yearsBeforeRetirement,
                yearsToRetirement,
                yearsInRetirement,
                yearsRemainingInRetirement,
                iraWithdrawalDate,
                earlySocialSecurityAge,
                earlySocialSecurityDate,
                medicareDate,
                retireAge,
                fullSocialSecurityAge,
                fullSocialSecurityDate,
                delayedSocialSecurityAge,
                delayedSocialSecurityDate,
                qualifiedCharitableDistributionDate,
                nonInheritedRMDDate,
                lifeExpectancyDate
            })
        } else {
            return null
        }
    }

    calculateNetWorth(liquidAssets: Asset[], realAssets: Asset[], personalAssets: Asset[], otherAssets: Asset[], liabilities: Liability[], snapshot: Snapshot): INetWorthCalc {
        const calc: INetWorthCalc = {
            id: snapshot.id,
            date: snapshot.date,
            liquidAssetsTotal: liquidAssets.reduce((accum: number, item: Asset) => {
                if (item.netWorthExcluded) return accum
                const balance = snapshot.getSnapshotBalance(item.id, "Asset") ?? 0
                return (accum + balance)
            }, 0),
            realAssetsTotal: realAssets.reduce((accum: number, item: Asset) => {
                if (item.netWorthExcluded) return accum
                const balance = snapshot.getSnapshotBalance(item.id, "Asset") ?? 0
                return (accum + balance)
            }, 0),
            personalAssetsTotal: personalAssets.reduce((accum: number, item: Asset) => {
                if (item.netWorthExcluded) return accum
                const balance = snapshot?.getSnapshotBalance(item.id, "Asset") ?? 0
                return (accum + balance)
            }, 0),
            otherAssetsTotal: otherAssets.reduce((accum: number, item: Asset) => {
                if (item.netWorthExcluded) return accum
                const balance = snapshot?.getSnapshotBalance(item.id, "Asset") ?? 0
                return (accum + balance)
            }, 0),
            liabilitiesTotal: liabilities.reduce((accum: number, item: Liability) => {
                const balance = snapshot?.getSnapshotBalance(item.id, "Liability") ?? 0
                return (accum + balance)
            }, 0),
            assetsTotal: 0,
            // Display-only: sum of excluded balances, used by the Net Worth Report footnote.
            // Section totals above already omit excluded items, so do NOT subtract this from netWorthTotal.
            excludedAssetsTotal: [...liquidAssets, ...realAssets, ...personalAssets, ...otherAssets].reduce((accum: number, item: Asset) => {
                if (!item.netWorthExcluded) return accum
                const balance = snapshot?.getSnapshotBalance(item.id, "Asset") ?? 0
                return (accum + balance)
            }, 0),
            netWorthTotal: 0
        }
        calc.assetsTotal = calc.liquidAssetsTotal + calc.realAssetsTotal + calc.personalAssetsTotal + calc.otherAssetsTotal
        calc.netWorthTotal = calc.assetsTotal - calc.liabilitiesTotal
        return calc
    }

    calculateBudget(year: number, model: Model, startAt?: Date, snapshot?: Snapshot): IBudgetCalc {
        this.currentYear = getDateToday().getFullYear()
        const details: IBudgetItemDetail[] = []
        const incomeSummary = this.getIncomeSummary(year, model, details, startAt, snapshot)
        const deductionSummary = this.getDeductionSummary(year, model, details, startAt, snapshot)
        const taxSummary = this.getTaxSummary(year, model, details, startAt, snapshot)
        const expenseSummary = this.getExpenseSummary(year, model, details, startAt, snapshot)
        details.sort((a: IBudgetItemDetail, b: IBudgetItemDetail) => compareAsc(a.date, b.date))

        const totalGuaranteedIncome = this.getGuaranteedIncome(incomeSummary)

        const budgetCalc: IBudgetCalc = {
            year,
            date: snapshot ? snapshot.date : new Date(year, 0, 1),
            incomeSummary,
            deductionSummary,
            taxSummary,
            expenseSummary,
            details,
            totalIncome: incomeSummary.total,
            totalGuaranteedIncome,
            totalDeductions: deductionSummary.total,
            totalTaxes: taxSummary.total,
            totalEstimatedTaxes: 0,
            totalEssentialExpenses: expenseSummary.essential,
            totalDiscretionaryExpenses: expenseSummary.discretionary,
            totalExpenses: expenseSummary.total,
            netIncome: incomeSummary.total - deductionSummary.total - taxSummary.total - expenseSummary.total,
            model: model
        }
        // Look for snapshot tax estimate
        if (snapshot) {
            const estimatedTax = snapshot.getSnapshotAmount("estimatedFederalTaxes", "Tax")
            if (estimatedTax > 0) {
                budgetCalc.totalEstimatedTaxes = estimatedTax
            }
        }

        return budgetCalc
    }

    getIncomeSummary(year: number, model: Model, details?: IBudgetItemDetail[], startAt?: Date, snapshot?: Snapshot): IIncomeSummary {
        const items = model.incomes
        const summary: IIncomeSummary = {
            year: year,
            total: 0,
            byItem: [],
            byType: [],
            byMonth: Array(12).fill(0, 0)
        }

        const startDate = startAt ?? new Date(year, 0, 1)
        let endDate = new Date(year, 11, 31)

        const ssOwnerMap = new Map<string, Income[]>()
        const filteredIncomes = items.filter((income: Income, index: number) => {
            const endYear = income.endYear
            if (!income.archived && income.overlapsWith(startDate, endDate) && (endYear === 0 || endYear >= year)) {
                // If it is social security income and the owner age is greater than life expectancy,
                // see if there is a spouse that should inherit it
                if (income.incomeType === IncomeType.SocialSecurityBenefits) {
                    if (income.ownerId) {
                        let record = ssOwnerMap.get(income.ownerId)
                        if (!record) {
                            ssOwnerMap.set(income.ownerId, [income])
                        } else {
                            record.push(income)
                        }
                    }
                    return false
                } else {
                    return true
                }
            }
            return false
        })

        // Handle social security inheritance
        if (ssOwnerMap.size === 1) {
            // Only one owner, so keep all
            ssOwnerMap.forEach((value: Income[], key: string) => {
                filteredIncomes.push(...value)
            })
        } else if (ssOwnerMap.size > 1) {
            const owners: Person[] = []
            ssOwnerMap.forEach((value: Income[], key: string) => {
                if (value.length > 0 && value[0].owner) {
                    owners.push(value[0].owner)
                }
            })
            if (owners && owners.length > 1) {
                const owner0 = owners[0]!
                const owner1 = owners[1]!
                const incomes0 = ssOwnerMap.get(owner0.id)
                const incomes1 = ssOwnerMap.get(owner1.id)
                if (incomes0 && incomes0.length > 0 && incomes1 && incomes1.length > 0) {
                    if (owner0.lifeExpectancyYear >= year) {
                        // Owner 1 is living
                        if (owner1.lifeExpectancyYear < year) {
                            // Owner 2 has died, so take the higher benefit
                            if (incomes0[0].amount > incomes1[0].amount) {
                                filteredIncomes.push(...incomes0)
                            } else {
                                filteredIncomes.push(...incomes1)
                            }
                        } else {
                            // Owner 2 is also living, so take both incomes
                            filteredIncomes.push(...incomes0)
                            filteredIncomes.push(...incomes1)
                        }
                    } else if (owner1.lifeExpectancyYear >= year) {
                        // Owner 2 is living
                        if (owner0.lifeExpectancyYear < year) {
                            // Owner 1 has died, so take the higher benefit
                            if (incomes0[0].amount > incomes1[0].amount) {
                                filteredIncomes.push(...incomes0)
                            } else {
                                filteredIncomes.push(...incomes1)
                            }
                        } else {
                            // Person 1 is also living, so take both incomes
                            filteredIncomes.push(...incomes0)
                            filteredIncomes.push(...incomes1)
                        }
                    }
                }
            }
        }

        filteredIncomes.forEach((item: Income) => {
            const snapshotDetail = snapshot ? snapshot.getSnapshotDetail(item) : undefined
            if (snapshot && !snapshotDetail) {
                return
            }
            let total = 0
            const occurrences = this.getScheduledOccurrences(item, startDate, endDate, snapshotDetail)
            for (const {date} of occurrences) {
                const futureAmount = this.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
                total += futureAmount
                if (details) {
                    details.push({
                        id: item.id,
                        date: date,
                        itemType: 'Income',
                        description: item.description,
                        amount: futureAmount
                    })
                }
                summary.byMonth[date.getMonth()] += futureAmount
            }

            if (item.incomeType) {
                let incomeTypeSummary = summary.byType.find((typeSummary: IIncomeTypeSummary) => typeSummary.incomeType === item.incomeType)
                if (incomeTypeSummary) {
                    incomeTypeSummary.total += total
                } else {
                    incomeTypeSummary = {
                        incomeType: item.incomeType,
                        total: total
                    }
                    summary.byType.push(incomeTypeSummary)
                }
            }

            summary.byItem.push({id: item.id, incomeType: item.incomeType, description: item.description, total: total, excludeFromNIIT: item.excludeFromNIIT})
            summary.total += total
        })

        return summary
    }

    getGuaranteedIncome(incomeSummary: IIncomeSummary) {
        let total = 0

        incomeSummary.byType.forEach((type: IIncomeTypeSummary) => {
            if (type.incomeType === IncomeType.SocialSecurityBenefits || type.incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable ||
              type.incomeType === IncomeType.PensionsAndAnnuitiesTaxable) {
                total += type.total
            }
        })

        return total
    }

    getDeductionSummary(year: number, model: Model, details?: IBudgetItemDetail[], startAt?: Date, snapshot?: Snapshot): IDeductionSummary {
        const items = model.deductions
        const summary: IDeductionSummary = {
            year: year,
            total: 0,
            byItem: [],
            byMonth: Array(12).fill(0, 0)
        }

        const startDate = startAt ?? new Date(year, 0, 1)
        let endDate = new Date(year, 11, 31)

        const filteredDeductions = items.filter((deduction: Deduction) => {
            const endYear = deduction.endYear
            return !deduction.archived && deduction.overlapsWith(startDate, endDate) && (endYear === 0 || endYear >= year)
        })
        filteredDeductions.forEach((item: Deduction) => {
            const snapshotDetail = snapshot ? snapshot.getSnapshotDetail(item) : undefined
            if (snapshot && !snapshotDetail) {
                return
            }
            const occurrences = this.getScheduledOccurrences(item, startDate, endDate, snapshotDetail)
            let total = 0
            for (const {date} of occurrences) {
                const futureAmount = this.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
                total += futureAmount
                if (details) {
                    details.push({
                        id: item.id,
                        date: date,
                        itemType: 'Deduction',
                        description: item.description,
                        amount: -futureAmount
                    })
                }
                summary.byMonth[date.getMonth()] += futureAmount
            }
            summary.byItem.push({id: item.id, description: item.description, total: total})
            summary.total += total
        })

        return summary
    }

    getTaxSummary(year: number, model: Model, details?: IBudgetItemDetail[], startAt?: Date, snapshot?: Snapshot): ITaxSummary {
        const items = model.taxes
        const summary: ITaxSummary = {
            year: year,
            total: 0,
            byItem: [],
            byType: [],
            byMonth: Array(12).fill(0, 0)
        }

        const startDate = startAt ?? new Date(year, 0, 1)
        let endDate = new Date(year, 11, 31)

        const filteredTaxes = items.filter((item: Tax) => {
            const endYear = item.endYear
            return !item.archived && item.overlapsWith(startDate, endDate) && (endYear === 0 || endYear >= year)
        })
        filteredTaxes.forEach((item: Tax) => {
            const snapshotDetail = snapshot ? snapshot.getSnapshotDetail(item) : undefined
            if (snapshot && !snapshotDetail) {
                return
            }
            const occurrences = this.getScheduledOccurrences(item, startDate, endDate, snapshotDetail)
            let total = 0
            for (const {date} of occurrences) {
                const futureAmount = this.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
                total += futureAmount
                if (details) {
                    details.push({
                        id: item.id,
                        date: date,
                        itemType: 'Tax Withholding',
                        description: item.description,
                        amount: -futureAmount
                    })
                }
                summary.byMonth[date.getMonth()] += futureAmount
            }
            if (item.taxType) {
                let taxTypeSummary = summary.byType.find((typeSummary: ITaxTypeSummary) => typeSummary.taxType === item.taxType)
                if (taxTypeSummary) {
                    taxTypeSummary.total += total
                } else {
                    taxTypeSummary = {
                        taxType: item.taxType,
                        total: total
                    }
                    summary.byType.push(taxTypeSummary)
                }
            }
            summary.byItem.push({id: item.id, description: item.description, total: total})
            summary.total += total
        })

        return summary
    }

    getExpenseSummary(year: number, model: Model, details?: IBudgetItemDetail[], startAt?: Date, snapshot?: Snapshot): IExpenseSummary {
        const items = model.expenses
        const summary: IExpenseSummary = {
            year: year,
            total: 0,
            essential: 0,
            discretionary: 0,
            byCategory: [],
            byMonth: Array(12).fill(0, 0),
            byMonthEssential: Array(12).fill(0, 0),
            byMonthDiscretionary: Array(12).fill(0, 0),
            withdrawals: []
        }

        const startDate = startAt ?? new Date(year, 0, 1)
        let endDate = new Date(year, 11, 31)

        const scheduledExpenses = items.filter((expense: Expense) => {
            const endYear = expense.endYear
            return !expense.archived && expense.overlapsWith(startDate, endDate) && (endYear === 0 || endYear >= year)
        })

        for (let category = ExpenseCategory.General; category <= ExpenseCategory.GivingAndMisc; category++) {
            const categoryExpenses = scheduledExpenses.filter((expense: Expense) => expense.expenseCategory === category)

            let categoryTotal = 0
            let categoryByMonth = Array(12).fill(0, 0)
            let items: IExpenseItemSummary[] = []
            let essential = 0
            let discretionary = 0
            categoryExpenses.forEach((item: Expense) => {
                const snapshotDetail = snapshot ? snapshot.getSnapshotDetail(item) : undefined
                if (snapshot && !snapshotDetail) {
                    return
                }
                const occurrences = this.getScheduledOccurrences(item, startDate, endDate, snapshotDetail)
                let total = 0
                for (const {date} of occurrences) {
                    const futureAmount = this.getItemFutureValue(item, date, model.inflationStrategy, snapshotDetail)
                    total += futureAmount
                    if (details) {
                        details.push({
                            id: item.id,
                            date: date,
                            itemType: 'Expense',
                            discretionary: item.discretionary,
                            description: item.description,
                            amount: -futureAmount
                        })
                    }
                    summary.byMonth[date.getMonth()] += futureAmount
                    categoryByMonth[date.getMonth()] += futureAmount
                    if (item.discretionary) {
                        summary.discretionary += futureAmount
                        discretionary += futureAmount
                        summary.byMonthDiscretionary[date.getMonth()] += futureAmount
                    } else {
                        summary.essential += futureAmount
                        essential += futureAmount
                        summary.byMonthEssential[date.getMonth()] += futureAmount
                    }
                }
                categoryTotal += total
                items.push({id: item.id, description: item.description, total: total})
                if (item.assetId) {
                    let withdrawal = summary.withdrawals.find((w: IExpenseWithdrawal) => w.assetId === item.assetId)
                    if (withdrawal) {
                        withdrawal.amount += total
                    } else {
                        summary.withdrawals.push({assetId: item.assetId, amount: total})
                    }
                }
            })
            summary.byCategory.push(
              {
                  category: category,
                  total: categoryTotal,
                  essential: essential,
                  discretionary: discretionary,
                  byItem: items,
                  byMonth: categoryByMonth
              })
            summary.total += categoryTotal
        }

        return summary
    }

    getItemFutureValue(item: any, futureDate: Date, inflationStrategy?: IInflationStrategy, snapshotDetail?: ISnapshotDetail) {
        // if (!snapshotDetail) {
        //     return 0
        // }
        let presentValue = snapshotDetail ? snapshotDetail.amount : item.amount
        let annualInf: number
        let customInflation: number[] | undefined = undefined

        if (item instanceof Deduction) {
            if (item.annualInf !== null && item.annualInf !== undefined && (item.infLock || !inflationStrategy || !inflationStrategy.deduction.lock)) {
                annualInf = item.annualInf
            } else if (inflationStrategy) {
                annualInf = inflationStrategy.deduction.rate
            } else {
                annualInf = this.defaultInf
            }
        } else if (item instanceof Expense) {
            if ((item as Expense).expenseCategory === ExpenseCategory.LoansAndLiabilities) {
                annualInf = 0
            } else if (item.annualInf !== null && item.annualInf !== undefined && (item.infLock || !inflationStrategy || !inflationStrategy.expense.lock)) {
                annualInf = item.annualInf
            } else if (inflationStrategy) {
                annualInf = inflationStrategy.expense.rate
                if (inflationStrategy.expense.custom) {
                    customInflation = inflationStrategy.expense.custom
                }
            } else {
                annualInf = this.defaultInf
            }
        } else if (item instanceof Income) {
            if (item.annualInf !== null && item.annualInf !== undefined && (item.infLock || !inflationStrategy || !inflationStrategy.income.lock)) {
                annualInf = item.annualInf
            } else if (inflationStrategy) {
                annualInf = inflationStrategy.income.rate
            } else {
                annualInf = this.defaultInf
            }
        } else if (item instanceof Tax) {
            if (item.annualInf !== null && item.annualInf !== undefined && (item.infLock || !inflationStrategy || !inflationStrategy.tax.lock)) {
                annualInf = item.annualInf
            } else if (inflationStrategy) {
                annualInf = inflationStrategy.tax.rate
            } else {
                annualInf = this.defaultInf
            }
        } else {
            annualInf = this.defaultInf
        }

        // Calculate survivor percent
        if (item.incomeTypeDef) {
            if (item.incomeTypeDef.requiredExtraFields &&
              (item.incomeTypeDef.requiredExtraFields.includes("survivorAnnuityPercent")) &&
              item.survivorPercent !== undefined) {
                let lifeExpectancyDate = isoToLocalDate(item.owner.lifeExpectancyDate)
                if (lifeExpectancyDate && futureDate.getTime() > lifeExpectancyDate.getTime()) {
                    presentValue = presentValue * (item.survivorPercent / 100.0)
                }
            }
        }
        let futureValue = presentValue

        if (annualInf !== 0) {
            if (item.annualInfSchedule) {
                const futureYear = futureDate.getFullYear()
                for (let year = this.currentYear + 1; year <= futureYear; year++) {
                    const rate = item.annualInfSchedule.get(year) ?? annualInf
                    futureValue += futureValue * rate
                }
            } else if (customInflation) {
                const years = futureDate.getFullYear() - this.currentYear
                for (let year = 0; year < years; year++) {
                    const index = year % customInflation.length
                    const rate = customInflation[index]
                    futureValue += futureValue * rate
                }
            } else {
                const years = futureDate.getFullYear() - this.currentYear
                if (years > 0) {
                    futureValue = this.futureValue(presentValue, annualInf, years)
                }
            }
        }

        return futureValue
    }

    futureValue(presentValue: number, infRate: number, periods: number): number {
        if (infRate === 0) {
            return presentValue
        } else {
            const futureValue = presentValue * Math.pow(1.0 + infRate, periods)
            return roundMoney(futureValue)
        }
    }

    getRSchedule(item: Scheduled, snapshotDetail?: ISnapshotDetail) {
        const startDate = item.startDate ?? new Date()
        let endDate = item.endDate
        const schedule = snapshotDetail && snapshotDetail.schedule ? snapshotDetail.schedule : item.schedule
        let frequency = schedule.frequency
        if (frequency === Frequency.Once) {
            frequency = Frequency.Monthly
            endDate = startDate
        }
        // let byDayOfMonth: RuleOption.ByDayOfMonth | undefined

        let byMonthOfYear: RuleOption.ByMonthOfYear[] | undefined
        if (frequency === Frequency.Yearly) {
            if (schedule.each && schedule.each.length > 0) {
                byMonthOfYear = item.schedule.each as RuleOption.ByMonthOfYear[]
            } else {
                byMonthOfYear = [startDate.getMonth() + 1] as RuleOption.ByMonthOfYear[]
            }
        }

        let byDayOfMonth: RuleOption.ByDayOfMonth[] | undefined
        if (schedule.frequency === Frequency.Monthly || schedule.frequency === Frequency.Yearly) {
            if (schedule.on && schedule.on.length > 0 && typeof (schedule.on[0]) === "number") {
                byDayOfMonth = schedule.on as RuleOption.ByDayOfMonth[]
            } else {
                byDayOfMonth = [-1]
            }
        }

        let byDayOfWeek: RuleOption.ByDayOfWeek[] | undefined
        if (schedule.frequency === Frequency.Weekly) {
            if (schedule.on && schedule.on.length > 0 && typeof (schedule.on[0]) === "string") {
                byDayOfWeek = schedule.on as RuleOption.ByDayOfWeek[]
            } else {
                byDayOfWeek = ['MO'] as RuleOption.ByDayOfWeek[]
            }
        }

        const rSchedule = new RSchedule({
            rrules: [
                {
                    frequency: frequency.toUpperCase() as RuleOption.Frequency,
                    start: startDate,
                    end: endDate,
                    interval: schedule.interval,
                    byDayOfWeek: byDayOfWeek,
                    byDayOfMonth: byDayOfMonth,
                    byMonthOfYear: byMonthOfYear
                }
            ]
        })

        return rSchedule
    }

    getScheduledOccurrences(item: Scheduled, startDate: Date, endDate: Date, snapshotDetail?: ISnapshotDetail) {
        if (this.occurencesCache) {
            const key = `${(item as unknown as IModelItem).id}/${getISODateFromDate(startDate)}/${getISODateFromDate(endDate)}/${(item as unknown as IModelItem).updatedAt}`
            let occurrences = this.occurencesCache.get(key)
            if (!occurrences) {
                const rSchedule = this.getRSchedule(item, snapshotDetail)
                occurrences = rSchedule.occurrences({
                    start: startDate,
                    end: endDate,
                    take: 1000
                }).toArray().map((occurrence: any) => {
                    return occurrence
                })

                this.occurencesCache.set(key, occurrences)
            }

            return occurrences
        } else {
            const rSchedule = this.getRSchedule(item, snapshotDetail)
            const occurrences = rSchedule.occurrences({
                start: startDate,
                end: endDate,
                take: 1000
            }).toArray().map((occurrence: any) => {
              return occurrence
            })
            return occurrences
        }
    }

    calculateFutureKey(model: Model, endYear?: number) {
        const key = `${model.id}/${model.updatedAt}/${model.appliedPlan ? model.appliedPlan.id : ""}/${model.appliedPlan ? model.appliedPlan.updatedAt : ""}/${endYear}`
        return key
    }

    calculateFuture(model: Model, label?: string, endYear?: number) {
        const key = this.calculateFutureKey(model, endYear)
        if (this.lastFutureCalc && key === this.lastFutureCalc.key) {
            // console.log(`Returning lastFutureCalc`)
            return this.lastFutureCalc
        }
        // const startTime = new Date()

        const futureCalc = new FutureCalc()
        futureCalc.label = label ?? model.appliedPlan?.name ?? ""
        futureCalc.key = key
        futureCalc.model = model
        futureCalc.plan = model.appliedPlan

        const calYear = (new Date()).getFullYear()
        const persons = model.persons
        if (persons.length > 0) {
            futureCalc.person1 = persons[0]
            futureCalc.maxAge = futureCalc.person1!.getAge()
        }
        if (persons.length > 1) {
            futureCalc.person2 = persons[1]
            futureCalc.maxAge = Math.max(futureCalc.maxAge, futureCalc.person2!.getAge())
        }
        futureCalc.latestRetirementYear = model.persons[0].latestRetireYear
        futureCalc.latestLifeExpectancyYear = model.persons[0].latestLifeExpectancyYear

        futureCalc.liquidAssets = model.assets.filter((a: Asset) => a.assetCategory === AssetCategory.LiquidInvestableAssets && !a.archived && !a.netWorthExcluded).map((asset: Asset) => {
            return new LiquidAsset(asset)
        })
        this.orderLiquidAssets(futureCalc.liquidAssets)

        futureCalc.liquidAssets.forEach((liquidAsset: LiquidAsset) => {
            const typeDef = liquidAsset.assetTypeDef
            if (liquidAsset.ownerId && !liquidAsset.owner) {
                liquidAsset.owner = model.persons.find((p: Person) => p.id === liquidAsset.ownerId)
            }
            if (liquidAsset.owner) {
                liquidAsset.ownerAge = liquidAsset.owner.getAge(calYear)
                if (liquidAsset.owner.spouse) {
                    liquidAsset.spouseAge = liquidAsset.owner.spouse.getAge(calYear)
                }
                if (typeDef.hasRMD) {
                    if (typeDef.rmdBeginAge === 73 && 1960 <= liquidAsset.owner.birthYear) {
                        liquidAsset.rmdBeginAge = 75
                    } else {
                        liquidAsset.rmdBeginAge = typeDef.rmdBeginAge
                    }
                    if (typeDef.use10YDist) {
                        const iy = liquidAsset.inheritedYear
                        liquidAsset.inheritedYear = (iy <= calYear && iy >= calYear - 10) ? iy : calYear
                        liquidAsset.distYearsLeft = liquidAsset.inheritedYear - calYear + 10
                    }
                    if (typeDef.useTable1Once) {
                        const iy = liquidAsset.inheritedYear
                        liquidAsset.inheritedYear = iy <= 2019 ? iy : 2019

                        // Compute distPeriodStart
                        const ownerAgeOnOriginalOwnerDeathYear = liquidAsset.ownerAge - (calYear - liquidAsset.inheritedYear)
                        const originalOwnerAgeOnDeathYear = liquidAsset.inheritedYear - liquidAsset.originalOwnerBirthYear
                        const minAgeOnDeathYear = Math.min(ownerAgeOnOriginalOwnerDeathYear, originalOwnerAgeOnDeathYear)
                        const distPeriodAtAgeInherited = TABLE1_1D[Math.max(0, Math.min(minAgeOnDeathYear, 120))]
                        liquidAsset.distPeriodStart = distPeriodAtAgeInherited - (calYear - liquidAsset.inheritedYear)
                    }
                }

                if (typeDef.isUnqualified) {
                    futureCalc.nonQualifiedAssets.push(liquidAsset)
                }
                if (typeDef.isIRA) {
                    futureCalc.iraAssets.push(liquidAsset)
                    if (typeDef.isTaxableIRA) {
                        futureCalc.taxableIraAssets.push(liquidAsset)
                    }
                }
            }
        })

        futureCalc.originalLiquidAssetTotal = futureCalc.liquidAssets.reduce((accum: number, item: Asset) => {
            return (accum + item.balance)
        }, 0)

        futureCalc.originalLiabilityTotal = model.liabilities.reduce((accum: number, item: Liability) => {
            return (accum + item.balance)
        }, 0)

        futureCalc.realAssets = model.assets.filter((a: Asset) => (a.assetCategory === AssetCategory.RealEstateAndProperty || a.assetCategory === AssetCategory.OtherAssets) && !a.archived && !a.netWorthExcluded)
        futureCalc.liabilities = model.liabilities.filter((l: Liability) => !l.archived)

        futureCalc.snapshots = [...model.snapshots]
        futureCalc.incomes = model.incomes.filter((i: Income) => !i.archived)
        futureCalc.activeIncomes = [...futureCalc.incomes]
        futureCalc.deductions = model.deductions.filter((d: Deduction) => !d.archived)
        futureCalc.taxes = model.taxes.filter((t: Tax) => !t.archived)

        if (model.growthStrategy) {
            futureCalc.growthStrategy = model.growthStrategy
        }

        let year = getDateToday().getFullYear()
        this.currentYear = year
        futureCalc.startYear = year
        futureCalc.endYear = endYear ? endYear : futureCalc.latestLifeExpectancyYear

        futureCalc.benchmarkData.latestRetirementYear = futureCalc.latestRetirementYear
        futureCalc.benchmarkData.startYear = futureCalc.latestRetirementYear ? Math.max(futureCalc.latestRetirementYear - calYear + 2, 0) : 999
        futureCalc.benchmarkData.endYear = futureCalc.latestRetirementYear ? futureCalc.latestLifeExpectancyYear - calYear + 1 : 999

        let futureYear
        while (year <= futureCalc.endYear) {
            futureYear = this.calculateFutureYear(year, model, futureCalc)
            futureCalc.futureYears.push(futureYear)

            if (futureYear.taxEstimate) {
                futureCalc.totalTax += futureYear.taxEstimate.totalEstimatedTax
            }
            ++year
        }

        if (futureYear) {
            futureCalc.netLiquidAssets = futureYear.netLiquidAssets
            futureCalc.netRealAssets = futureYear.netRealAssets
            futureCalc.netLiabilities = futureYear.netLiabilities
        }

        // const endTime = new Date()
        // console.debug(`calculateFuture for ${model.name} ${model.updatedAt}: ${(endTime.getTime() - startTime.getTime()) / 1000} seconds`)

        this.lastFutureCalc = futureCalc
        return futureCalc
    }

    recalculateFuture(priorCalc: FutureCalc, label: string) {
        // const startTime = new Date()

        const futureCalc = clone(priorCalc)
        futureCalc.label = label
        futureCalc.futureYears = []
        futureCalc.totalTax = 0
        futureCalc.netLiquidAssets = 0
        futureCalc.netRealAssets = 0
        futureCalc.netLiabilities = 0

        const model = futureCalc.model

        let year = getDateToday().getFullYear()

        let futureYear
        while (year <= futureCalc.endYear) {
            futureYear = this.calculateFutureYear(year, model!, futureCalc)
            futureCalc.futureYears.push(futureYear)

            if (futureYear.taxEstimate) {
                futureCalc.totalTax += futureYear.taxEstimate.totalEstimatedTax
            }
            ++year
        }

        if (futureYear) {
            futureCalc.netLiquidAssets = futureYear.netLiquidAssets
            futureCalc.netRealAssets = futureYear.netRealAssets
            futureCalc.netLiabilities = futureYear.netLiabilities
        }

        // const endTime = new Date()
        // console.debug(`recalculateFuture for ${model!.name} ${model!.updatedAt}: ${(endTime.getTime() - startTime.getTime()) / 1000} seconds`)

        return futureCalc
    }

    calculateFutureYear(calcYear: number, model: Model, futureCalc: FutureCalc, depth: number = 0, prevShortfall: number = 0) : IFutureYear {
        // console.debug(`calculateFutureYear: ${calcYear}`)
        const prevYear = futureCalc.futureYears.length > 0 ? futureCalc.futureYears[futureCalc.futureYears.length - 1] : undefined
        const yearsPast = calcYear - futureCalc.startYear
        const yearNum = yearsPast + 1
        const person1 = futureCalc.person1
        const person2 = futureCalc.person2
        // model.incomes.sort((a: Income, b: Income) => a.description.localeCompare(b.description))
        const liquidAssets = futureCalc.liquidAssets
        const realAssets = futureCalc.realAssets
        const deductions = futureCalc.deductions
        const liabilities = futureCalc.liabilities

        let budgetCalc: IBudgetCalc
        if (this.budgetCache) {
          const foundCalc = this.budgetCache.get(calcYear.toString())
          if (foundCalc) {
              budgetCalc = foundCalc
              // console.log(`BudgetCache hit for ${calcYear}`)
          } else {
              // console.log(`BudgetCache miss for ${calcYear}`)
              budgetCalc = this.calculateBudget(calcYear, model)
              this.budgetCache.set(calcYear.toString(), budgetCalc)
          }
        } else {
            budgetCalc = this.calculateBudget(calcYear, model)
        }

        let proRateBudget: IBudgetCalc | undefined
        const startOfYear = new Date(calcYear, 0, 1)
        let startAt = startOfYear
        let snapshot: Snapshot | undefined
        if (!prevYear) {
            // Calculate remaining expenses based on current (formerly last saved) snapshot in the current year
            if (futureCalc.snapshots.length > 0 && /*futureCalc.snapshots[0].id !== "current" &&*/ compareAsc(startAt, futureCalc.snapshots[0].date) <= 0) {
                snapshot = futureCalc.snapshots[0]
            }
            if (snapshot) {
                startAt = snapshot.date

                if (this.budgetCache) {
                    const key = getISODateTimeFromDate(startAt)
                    proRateBudget = this.budgetCache.get(key)
                    if (proRateBudget) {
                        // console.log(`BudgetCache hit for ${key}`)
                    } else {
                        // console.log(`BudgetCache miss for ${key}`)
                        proRateBudget = this.calculateBudget(calcYear, model, startAt)
                        this.budgetCache.set(key, proRateBudget)
                    }
                } else {
                    proRateBudget = this.calculateBudget(calcYear, model, startAt)
                }

            }
        }

        const thisYear: IFutureYear = {
            yearNum: yearNum,
            year: calcYear,
            startAt: startAt,
            person1Age: person1 ? person1.getAge(calcYear) : 0,
            person2Age: person2 ? person2.getAge(calcYear) : 0,
            expectedMaritalStatus: model.getExpectedMaritalStatus(calcYear),
            growthRate: 0,
            grossIncome: 0,
            remainingIncome: 0,
            guaranteedIncome: 0,
            deductions: 0,
            remainingDeductions: 0,
            taxes: 0,
            remainingTaxes: 0,
            taxRate: 0,
            netIncome: 0,
            allExpenses: 0,
            remainingExpenses: 0,
            essentialExpenses: 0,
            discretionaryExpenses: 0,
            expenseDistributions: 0,
            remainingDistributions: 0,
            rmds: 0,
            remainingRMDs: 0,
            contributions: 0,
            surplus: 0,
            remainingSurplus: 0,
            iraDistributions: 0,
            taxableIraDistributions: 0,
            unqualifiedDistributions: 0,
            qualifiedCapitalGainsDistributions: 0,
            allDistributions: 0,
            currentLiquidAssets: 0,
            netLiquidAssets: 0,
            currentRealAssets: 0,
            netRealAssets: 0,
            currentLiabilities: 0,
            netLiabilities: 0,
            yoyChange: 0,
            withdrawalRate: 0,
            incomeDetails: [],
            expenseDetails: [],
            expenseCategorySummary: [],
            assetDetails: [],
            realDetails: [],
            liabilityDetails: [],
            budgetCalc: budgetCalc,
            proRateBudget: proRateBudget,
            conversions: [],
            unaccounted: 0
        }

        const assetIncomeEnabled = futureCalc.growthStrategy.assetIncomeEnabled

        if (futureCalc.growthStrategy.growthModel === GrowthModel.Simple) {
            thisYear.growthRate = futureCalc.growthStrategy.growthRate
        } else if (futureCalc.growthStrategy.growthModel === GrowthModel.Custom && futureCalc.growthStrategy.customModel) {
            const customModel = futureCalc.growthStrategy.customModel
            const index = yearsPast % customModel.length
            thisYear.growthRate = customModel[index]
        } else {
            try {
                const index = yearsPast % 47
                thisYear.growthRate = this.rateModels.getRate(index, futureCalc.growthStrategy.returnSequence, futureCalc.growthStrategy.growthModel as unknown as RateModel)
                // const index = yearsPast % 40
                // thisYear.growthRate = ComplexGrowthTable[String(index)][futureCalc.growthStrategy.growthModel][futureCalc.growthStrategy.returnSequence]
            } catch (err) {
                thisYear.growthRate = futureCalc.growthStrategy.growthRate
            }
        }

        const expenseWithdrawals = budgetCalc.expenseSummary.withdrawals
        const remainingWithdrawals = proRateBudget ? proRateBudget.expenseSummary.withdrawals : []

        if (!prevYear) {
            liquidAssets.forEach((liquidAsset: LiquidAsset) => {
                let growthRate = ((!liquidAsset.rateLock && futureCalc.growthStrategy.ignoreCustomGrowthRates) || !isNumber(liquidAsset.returnRate)) ?
                  thisYear.growthRate : liquidAsset.returnRate
                let incomeRate = 0
                let basis = 0

                // Assume taxable asset reinvestment
                if (assetIncomeEnabled && liquidAsset.assetType === AssetType.Brokerage) {
                  if (liquidAsset.assetIncome && liquidAsset.assetIncome.basis) {
                    basis = liquidAsset.assetIncome.basis
                  }
                }

                const boyBalance = this.getBoYAssetBalance(calcYear, liquidAsset, snapshot)
                const currentBalance = this.getCurrentAssetBalance(calcYear, liquidAsset, snapshot)
                thisYear.currentLiquidAssets += currentBalance
                const ad: IAssetDetail = {
                    liquidAsset: liquidAsset,
                    boyBalance: boyBalance,
                    ytdBalance: currentBalance,
                    contribution: 0,
                    conversion: 0,
                    rmd: 0,
                    remainingRMD: 0,
                    withdrawals: 0,
                    distribution: 0,
                    balance: currentBalance,
                    proportion: 0,
                    incomeRate: incomeRate,
                    growthRate: growthRate,
                    eoyBalance: currentBalance,
                    withdrawalRate: 0,
                    yoyChange: 0,
                    boyBasis: basis,
                    eoyBasis: basis
                }

                if (liquidAsset.withdrawalsYTD !== 0) {
                    const withdrawal = liquidAsset.withdrawalsYTD
                    ad.withdrawals = -withdrawal
                    ad.distribution = -withdrawal
                    thisYear.allDistributions += withdrawal
                    const proRateFactor = 1 - this.proRateGrowthFactor(yearNum, startOfYear, thisYear.year, startAt)
                    const typeDef = liquidAsset.assetTypeDef
                    if (typeDef.isIRA) {
                        thisYear.iraDistributions += withdrawal
                        if (typeDef.isTaxableIRA) {
                            thisYear.taxableIraDistributions += withdrawal
                        }
                    } else if (typeDef.isUnqualified) {
                        thisYear.unqualifiedDistributions += withdrawal
                    } else if (typeDef.taxable) {
                      // Determine taxable income
                      const assetIncome = liquidAsset.assetIncome
                      if (assetIncome && assetIncome.details) {

                        const taxableInterest = this.getAssetIncomeDetail(liquidAsset, IncomeType.TaxableInterest)
                        if (taxableInterest && taxableInterest.rate) {
                          let income = Math.round(liquidAsset.withdrawalsYTD * taxableInterest.rate * proRateFactor)
                          if (income > 0) {
                            thisYear.incomeDetails.push({
                              id: liquidAsset.id,
                              description: `${liquidAsset.description} interest (${numberToPercentFormat(taxableInterest.rate, 2)})`,
                              incomeType: IncomeType.TaxableInterest,
                              amount: income,
                              remaining: 0
                            })
                            thisYear.grossIncome += income
                            // TODO: Consider if we need to handle reinvestment
                          }
                        }

                        const qualifiedDividends = this.getAssetIncomeDetail(liquidAsset, IncomeType.QualifiedDividends)
                        if (qualifiedDividends && qualifiedDividends.rate) {
                          let income = Math.round(liquidAsset.withdrawalsYTD * qualifiedDividends.rate * proRateFactor)
                          if (income > 0) {
                            thisYear.incomeDetails.push({
                              id: liquidAsset.id,
                              description: `${liquidAsset.description} qualified dividends (${numberToPercentFormat(qualifiedDividends.rate, 2)})`,
                              incomeType: IncomeType.QualifiedDividends,
                              amount: income,
                              remaining: 0
                            })
                            thisYear.grossIncome += income
                          }
                        }

                        const nonQualifiedDividends = this.getAssetIncomeDetail(liquidAsset, IncomeType.NonqualifiedDividends)
                        if (nonQualifiedDividends && nonQualifiedDividends.rate) {
                          let income = Math.round(liquidAsset.withdrawalsYTD * nonQualifiedDividends.rate * proRateFactor)
                          if (income > 0) {
                            thisYear.incomeDetails.push({
                              id: liquidAsset.id,
                              description: `${liquidAsset.description} non-qualified dividends (${numberToPercentFormat(nonQualifiedDividends.rate, 2)})`,
                              incomeType: IncomeType.NonqualifiedDividends,
                              amount: income,
                              remaining: 0
                            })
                            thisYear.grossIncome += income
                          }
                        }
                      }
                    }
                }

                thisYear.assetDetails.push(ad)
            })

            realAssets.forEach((realAsset: Asset) => {
                const growthRate = isNumber(realAsset.returnRate) ? realAsset.returnRate : 0
                const boyBalance = this.getBoYAssetBalance(calcYear, realAsset, snapshot)
                thisYear.currentRealAssets += boyBalance
                const rd: IRealAssetDetail = {
                    realAsset: realAsset,
                    conversion: 0,
                    balance: boyBalance,
                    boyBalance: boyBalance,
                    growthRate: growthRate,
                    eoyBalance: boyBalance,
                    yoyChange: 0
                }
                thisYear.realDetails.push(rd)
            })

            liabilities.forEach((liability: Liability) => {
                const endYear = liability.endYear
                if (!endYear || calcYear <= endYear) {
                    let interestRate = (isNumber(liability.interestRate) ? liability.interestRate : 0)
                    const boyBalance = this.getBoYLiabilityBalance(calcYear, liability, futureCalc.snapshots)
                    const expense = this.getLiabilityExpense(model, liability.id)
                    const expenseId = expense ? expense.id : undefined
                    const payments = expenseId ? this.getLiabilityPaymentTotal(budgetCalc, expenseId) : 0
                    const eoyBalance = (!endYear || calcYear < endYear) ? Math.max(boyBalance * (1 + interestRate) + payments, 0) : 0
                    thisYear.currentLiabilities += boyBalance
                    const ld: ILiabilityDetail = {
                        liability: liability,
                        expenseId: expenseId,
                        interestRate: liability.interestRate,
                        payments: payments,
                        boyBalance: boyBalance,
                        eoyBalance: eoyBalance
                    }
                    thisYear.liabilityDetails.push(ld)
                    thisYear.netLiabilities += eoyBalance
                }
            })
        } else {
            thisYear.currentLiquidAssets = prevYear.netLiquidAssets
            prevYear.assetDetails.forEach((detail: IAssetDetail) => {
              let growthRate = ((!detail.liquidAsset.rateLock && futureCalc.growthStrategy.ignoreCustomGrowthRates) || !isNumber(detail.liquidAsset.returnRate)) ?
                  thisYear.growthRate : detail.liquidAsset.returnRate
              let incomeRate = detail.incomeRate

              let boyBalance = detail.eoyBalance
              let basis = detail.eoyBasis
                const startDate = detail.liquidAsset.startDate
                if (startDate && startDate.getFullYear() === calcYear) {
                    // This is a future asset starting this calcYear
                    boyBalance = this.getBoYAssetBalance(calcYear, detail.liquidAsset)
                    const proRateFactor = this.proRateGrowthFactor(1, startDate, thisYear.year, startDate)
                    growthRate *= proRateFactor
                }

                thisYear.assetDetails.push({
                    liquidAsset: detail.liquidAsset,
                    boyBalance: boyBalance,
                    ytdBalance: boyBalance,
                    contribution: 0,
                    conversion: 0,
                    rmd: 0,
                    remainingRMD: 0,
                    withdrawals: 0,
                    distribution: 0,
                    balance: boyBalance,
                    proportion: 0,
                    incomeRate: incomeRate,
                    growthRate: growthRate,
                    eoyBalance: boyBalance,
                    withdrawalRate: 0,
                    yoyChange: detail.liquidAsset.balance !== 0 ? detail.eoyBalance / detail.liquidAsset.balance : 0,
                    boyBasis: basis,
                    eoyBasis: basis
                })
            })

            thisYear.currentRealAssets = prevYear.netRealAssets
            prevYear.realDetails.forEach((detail: IRealAssetDetail) => {
                const growthRate = isNumber(detail.realAsset.returnRate) ? detail.realAsset.returnRate : 0
                let boyBalance = detail.eoyBalance
                const startDate = detail.realAsset.startDate
                if (startDate && startDate.getFullYear() === calcYear) {
                    // This is a future asset starting this calcYear
                    boyBalance = this.getBoYAssetBalance(calcYear, detail.realAsset)
                }
                thisYear.realDetails.push({
                    realAsset: detail.realAsset,
                    conversion: 0,
                    boyBalance: boyBalance,
                    balance: boyBalance,
                    growthRate: growthRate,
                    eoyBalance: boyBalance,
                    yoyChange: 0 //detail.eoyBalance / detail.realAsset.balance
                })
            })

            thisYear.currentLiabilities = prevYear.netLiabilities
            prevYear.liabilityDetails.forEach((detail: ILiabilityDetail) => {
                let boyBalance = detail.eoyBalance
                const startDate = detail.liability.startDate
                if (startDate && startDate.getFullYear() === calcYear) {
                    // This is a future asset starting this calcYear
                    boyBalance = this.getBoYLiabilityBalance(calcYear, detail.liability, futureCalc.snapshots)
                }

                const endYear = detail.liability.endYear
                if (!endYear || calcYear <= endYear) {
                    const payments = detail.expenseId ? this.getLiabilityPaymentTotal(budgetCalc, detail.expenseId) : 0
                    const eoyBalance = (!endYear || calcYear < endYear) ? Math.max(boyBalance * (1 + detail.interestRate) + payments, 0) : 0
                    thisYear.currentLiabilities += boyBalance
                    const ld: ILiabilityDetail = {
                        liability: detail.liability,
                        interestRate: detail.interestRate,
                        expenseId: detail.expenseId,
                        payments: payments,
                        boyBalance: boyBalance,
                        eoyBalance: eoyBalance
                    }
                    thisYear.liabilityDetails.push(ld)
                    thisYear.netLiabilities += eoyBalance
                }
            })
        }

        thisYear.grossIncome = budgetCalc.totalIncome
        thisYear.remainingIncome = (yearNum === 1 && proRateBudget) ? proRateBudget.totalIncome : budgetCalc.totalIncome
        budgetCalc.incomeSummary.byItem.forEach((item: IIncomeItemSummary) => {
            let remaining: number = 0
            if (proRateBudget) {
                const proRateItem = proRateBudget.incomeSummary.byItem.find((it: IIncomeItemSummary) => it.id === item.id)
                if (proRateItem) {
                    remaining = proRateItem.total
                }
            }
            thisYear.incomeDetails.push({
                id: item.id,
                description: item.description,
                incomeType: item.incomeType,
                amount: item.total,
                remaining: remaining
            })
        })

        budgetCalc.expenseSummary.byCategory.forEach((summary: IExpenseCategorySummary) => {
            let remaining: number | undefined
            if (proRateBudget) {
                const proRateItem = proRateBudget.expenseSummary.byCategory.find((es: IExpenseCategorySummary) => es.category === summary.category)
                if (proRateItem) {
                    remaining = proRateItem.total
                }
            }
            thisYear.expenseDetails.push({
                category: summary.category,
                amount: summary.total,
                remaining: remaining
            })
            thisYear.expenseCategorySummary.push({  // Deprecated
                category: summary.category,
                total: summary.total,
                essential: summary.essential,
                discretionary: summary.discretionary,
                byItem: [],
                byMonth: []
            })
        })
        thisYear.deductions = budgetCalc.totalDeductions
        thisYear.guaranteedIncome = budgetCalc.totalGuaranteedIncome
        thisYear.netIncome = thisYear.grossIncome - thisYear.deductions - thisYear.taxes
        thisYear.allExpenses = budgetCalc.totalExpenses
        if (yearNum === 1 && proRateBudget) {
            thisYear.remainingExpenses = proRateBudget.totalExpenses
            thisYear.remainingDeductions = proRateBudget.totalDeductions
            // Assume elapsed (pre-snapshot) spending in excess of elapsed income was funded by
            // distributions. Keep recorded YTD withdrawals (accumulated above) if larger.
            const elapsedShortfall = (budgetCalc.totalExpenses - proRateBudget.totalExpenses)
                + (budgetCalc.totalDeductions - proRateBudget.totalDeductions)
                - (budgetCalc.totalIncome - proRateBudget.totalIncome)
            thisYear.allDistributions = Math.max(thisYear.allDistributions, elapsedShortfall, 0)
            thisYear.remainingTaxes = proRateBudget.totalTaxes
        } else {
            thisYear.remainingExpenses = thisYear.allExpenses
        }
        thisYear.taxes = budgetCalc.totalTaxes
        thisYear.essentialExpenses = budgetCalc.totalEssentialExpenses
        thisYear.discretionaryExpenses = budgetCalc.totalDiscretionaryExpenses
        deductions.forEach((d: Deduction) => {
            if (d.assetId && d.assetId !== "none") {
                // Find the deduction detail
                let deductionItemSummary = budgetCalc.deductionSummary.byItem.find((item: IDeductionItemSummary) => item.id === d.id)
                if (deductionItemSummary) {
                    thisYear.contributions += deductionItemSummary.total
                    // Find the asset detail
                    const assetDetail = thisYear.assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === d.assetId)
                    if (assetDetail) {
                        if (yearNum === 1 && proRateBudget) {
                            deductionItemSummary = proRateBudget.deductionSummary.byItem.find((item: IDeductionItemSummary) => item.id === d.id)
                            if (deductionItemSummary) {
                                // TODO: Handle growth based on periodic contributions
                                assetDetail.balance += deductionItemSummary.total
                                assetDetail.contribution += deductionItemSummary.total
                                if (assetDetail.liquidAsset.assetIncome?.basis !== null) {
                                  assetDetail.eoyBasis += deductionItemSummary.total
                                }
                            }
                        } else {
                            // TODO: Handle growth based on periodic contributions
                            assetDetail.balance += deductionItemSummary.total
                            assetDetail.contribution += deductionItemSummary.total
                            if (assetDetail.liquidAsset.assetIncome?.basis) {
                              assetDetail.eoyBasis += deductionItemSummary.total
                            }
                        }
                        // assetDetail.balance += deductionItemSummary.total
                    }
                }
            }
        })

        // Handle designated expense withdrawals
        expenseWithdrawals.forEach((withdrawal: IExpenseWithdrawal) => {
            const ad = thisYear.assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === withdrawal.assetId)
            if (ad) {
                const asset = ad.liquidAsset
                const typeDef = asset.assetTypeDef
                const balance = ad.balance
                const fullDistribution = Math.min(withdrawal.amount, ad.balance)
                if (fullDistribution > 0) {
                  if (yearNum === 1 && proRateBudget) {
                    ad.distribution -= fullDistribution
                    const remainingWithdrawal = remainingWithdrawals.find((w: IExpenseWithdrawal) => w.assetId === withdrawal.assetId)
                    let remainingAmount = remainingWithdrawal ? remainingWithdrawal.amount : 0
                    const remainingDistribution = Math.min(remainingAmount, ad.balance)
                    ad.balance -= remainingDistribution
                    ad.withdrawals -= Math.max(fullDistribution - remainingAmount, 0)
                    thisYear.expenseDistributions += fullDistribution
                    thisYear.remainingDistributions += remainingDistribution
                  } else {
                    ad.distribution -= fullDistribution
                    ad.balance -= fullDistribution
                    thisYear.expenseDistributions += fullDistribution
                  }
                  thisYear.allDistributions += fullDistribution
                  if (typeDef.isIRA) {
                    thisYear.iraDistributions += fullDistribution
                    if (typeDef.isTaxableIRA) {
                      thisYear.taxableIraDistributions += fullDistribution
                    }
                  } else if (typeDef.isUnqualified) {
                    thisYear.unqualifiedDistributions += fullDistribution
                  }
                }
            }
        })

        // Compute RMDs
        thisYear.assetDetails.forEach((ad: IAssetDetail) => {
            const liquidAsset = ad.liquidAsset
            const typeDef = liquidAsset.assetTypeDef
            if (typeDef.hasRMD && liquidAsset.ownerAge + yearsPast >= liquidAsset.rmdBeginAge) {
                let priorEoYBalance: number
                if (yearNum === 1 && liquidAsset.priorEoYBalance) {
                    priorEoYBalance = liquidAsset.priorEoYBalance
                } else {
                    priorEoYBalance = ad.boyBalance
                }
                let divisor = this.rmdDistributionPeriod(yearsPast, liquidAsset)
                let rmd = divisor > 0 ? Math.round(priorEoYBalance / divisor) : 0
                rmd = Math.min(rmd, ad.balance)
                ad.rmd = rmd
                let adjDistribution = 0
                if (yearNum === 1 && proRateBudget) {
                  if (ad.withdrawals < 0) {
                    // Adjust the withdrawal and prior distribution
                    if (rmd > -ad.withdrawals) {
                      // RMD exceeds withdrawals
                      adjDistribution = -ad.withdrawals
                      ad.remainingRMD = rmd + ad.withdrawals
                      ad.distribution += adjDistribution
                      ad.withdrawals += adjDistribution
                    } else {
                      // Withdrawals exceed RMD
                      ad.remainingRMD = 0
                      adjDistribution = rmd
                      ad.distribution += adjDistribution
                      ad.withdrawals += adjDistribution
                    }
                  } else {
                    ad.remainingRMD = rmd
                  }
                  // ad.remainingRMD = Math.max(rmd + ad.withdrawals, 0)
                  ad.balance -= ad.remainingRMD
                  thisYear.remainingRMDs += ad.remainingRMD
                  // thisYear.remainingDistributions += adjDistribution
                } else {
                  // if (ad.distribution < 0) {
                  //   // Adjust for distribution
                  //   if (rmd > -ad.distribution) {
                  //     // RMD exceeds distribution
                  //     adjDistribution = -ad.distribution
                  //     ad.balance += adjDistribution
                  //     ad.distribution += adjDistribution
                  //   } else {
                  //     // Distribution exceeds RMD
                  //     adjDistribution = -rmd
                  //     ad.balance += adjDistribution
                  //     ad.distribution += adjDistribution
                  //   }
                  // }
                  ad.balance -= rmd
                }
                thisYear.rmds += rmd
                thisYear.allDistributions += rmd - adjDistribution
                if (typeDef.isIRA) {
                    thisYear.iraDistributions += rmd - adjDistribution
                    if (typeDef.isTaxableIRA) {
                        thisYear.taxableIraDistributions += rmd - adjDistribution
                    }
                } else if (typeDef.isUnqualified) {
                    thisYear.unqualifiedDistributions += rmd - adjDistribution
                }
            }
        })

      // Handle previous calculated shortfall
      if (prevShortfall >= 1) {
        // Handle shortfall
        let shortfall = prevShortfall
        let assetGroups = this.groupAssetDetails(thisYear.assetDetails)
        // Check for sufficient assets to cover shortfall
        let totalAssets = assetGroups.reduce((accum: number, ag: IAssetDetailGroup) => accum + ag.balance, 0)
        if (totalAssets < shortfall) {
          // Not enough assets to cover shortfall
          // console.log(`Not enough assets ${totalAssets} to cover shortfall of ${shortfall} in year ${thisYear.year}`)
          // Check for any conversions scheduled for the year
          const conversions = model.assetConversions.filter((c: AssetConversion) => c.year === thisYear.year && (!c.archived || c.completedAt))
          if (conversions.length > 0) {
            // Apply conversions to try and cover the shortfall
            // console.debug(`Applying conversions to cover shortfall of ${shortfall} in year ${thisYear.year}`)
            this.applyFutureConversions(model, thisYear)
            // Rebuild asset groups
            assetGroups = this.groupAssetDetails(thisYear.assetDetails)
            totalAssets = assetGroups.reduce((accum: number, ag: IAssetDetailGroup) => accum + ag.balance, 0)
            if (totalAssets < shortfall) {
              // Still not enough assets to cover shortfall
              // console.log(`Still not enough assets ${totalAssets} to cover shortfall of ${shortfall} in year ${thisYear.year}`)
            }
          }
        }

        for (let ag of assetGroups) {
          if (shortfall > 0) {
            if (ag.balance > 0) {
              let distribution = Math.min(shortfall, ag.balance)
              // Proportional withdrawal
              for (let ad of ag.details) {
                const asset = ad.liquidAsset
                const typeDef = asset.assetTypeDef
                ad.proportion = ad.balance / ag.balance
                const detailDistribution = roundMoney(distribution * ad.proportion)

                ad.distribution -= detailDistribution
                ad.balance -= detailDistribution
                thisYear.allDistributions += detailDistribution
                if (typeDef.isIRA) {
                  thisYear.iraDistributions += detailDistribution
                  if (typeDef.isTaxableIRA) {
                    thisYear.taxableIraDistributions += detailDistribution
                  }
                } else if (typeDef.isUnqualified) {
                  thisYear.unqualifiedDistributions += detailDistribution
                }
              }
              ag.balance -= distribution
              thisYear.remainingDistributions += distribution
              shortfall -= distribution
            }
          }
        }
      }

      // Compute asset income (interest, dividends, capital growth)
      if (assetIncomeEnabled) {
        thisYear.assetDetails.forEach((ad: IAssetDetail) => {
          const asset = ad.liquidAsset
          if (asset.assetTypeDef.taxable) {
            let startDate = ad.liquidAsset.startDate
            if (startDate && startDate.getFullYear() <= thisYear.year) {
              const boyBalance = ad.boyBalance
              const balance = ad.balance
              const growthRate = ad.growthRate
              const proRateFactor = this.proRateGrowthFactor(yearNum, startDate, thisYear.year, startAt)
              ad.growthRate = growthRate * proRateFactor
              const assetIncome = asset.assetIncome
              if (assetIncome && assetIncome.details) {
                // const assetIncomeDate = asset.assetIncomeDate ? model.getDate(asset.assetIncomeDate) : undefined
                const rateOverrideFactor = growthRate / asset.returnRate
                const taxableInterest = this.getAssetIncomeDetail(asset, IncomeType.TaxableInterest)
                if (taxableInterest && taxableInterest.rate) {
                  const effectiveRate = taxableInterest.rate * rateOverrideFactor
                  let remainingRate = prevYear ? effectiveRate : effectiveRate * proRateFactor
                  let ytdRate = prevYear ? 0 : effectiveRate - remainingRate
                  let remainingIncome = roundMoney(balance * remainingRate)
                  let ytdIncome = roundMoney(boyBalance * ytdRate)
                  let totalIncome = ytdIncome + remainingIncome
                  if (remainingIncome > 0 || ytdIncome > 0) {
                    const incomeDetail = thisYear.incomeDetails.find((i: IIncomeDetail) => i.id === asset.id && i.incomeType === IncomeType.TaxableInterest)
                    if (incomeDetail) {
                      incomeDetail.amount += ytdIncome + remainingIncome
                      incomeDetail.remaining += remainingIncome
                    } else {
                      thisYear.incomeDetails.push({
                        id: asset.id,
                        description: `${asset.description} interest (${numberToPercentFormat( effectiveRate, 2)})`,
                        incomeType: IncomeType.TaxableInterest,
                        amount: ytdIncome + remainingIncome,
                        remaining: remainingIncome
                      })
                    }
                    thisYear.grossIncome += ytdIncome + remainingIncome

                    thisYear.deductions += totalIncome
                    thisYear.remainingDeductions += remainingIncome
                    thisYear.contributions += totalIncome
                    ad.contribution += remainingIncome
                    ad.balance += remainingIncome
                  }
                }

                const qualifiedDividends = this.getAssetIncomeDetail(asset, IncomeType.QualifiedDividends)
                if (qualifiedDividends && qualifiedDividends.rate) {
                  const effectiveRate = qualifiedDividends.rate * rateOverrideFactor
                  let remainingRate = prevYear ? effectiveRate : effectiveRate * proRateFactor
                  let ytdRate = prevYear ? 0 : effectiveRate - remainingRate
                  let remainingIncome = roundMoney(balance * remainingRate)
                  let ytdIncome = roundMoney(boyBalance * ytdRate)
                  let totalIncome = ytdIncome + remainingIncome
                  if (remainingIncome > 0 || ytdIncome > 0) {
                    const incomeDetail = thisYear.incomeDetails.find((i: IIncomeDetail) => i.id === asset.id && i.incomeType === IncomeType.QualifiedDividends)
                    if (incomeDetail) {
                      incomeDetail.amount += ytdIncome + remainingIncome
                      incomeDetail.remaining += remainingIncome
                    } else {
                      thisYear.incomeDetails.push({
                        id: asset.id,
                        description: `${asset.description} qualified dividends (${numberToPercentFormat( effectiveRate, 2)})`,
                        incomeType: IncomeType.QualifiedDividends,
                        amount: ytdIncome + remainingIncome,
                        remaining: remainingIncome
                      })
                    }
                    thisYear.grossIncome += ytdIncome + remainingIncome

                    thisYear.deductions += totalIncome
                    thisYear.remainingDeductions += remainingIncome
                    thisYear.contributions += totalIncome
                    ad.contribution += remainingIncome
                    ad.balance += remainingIncome
                  }
                }

                const nonQualifiedDividends = this.getAssetIncomeDetail(asset, IncomeType.NonqualifiedDividends)
                if (nonQualifiedDividends && nonQualifiedDividends.rate) {
                  const effectiveRate = nonQualifiedDividends.rate * rateOverrideFactor
                  let remainingRate = prevYear ? effectiveRate : effectiveRate * proRateFactor
                  let ytdRate = prevYear ? 0 : effectiveRate - remainingRate
                  let remainingIncome = roundMoney(balance * remainingRate)
                  let ytdIncome = roundMoney(boyBalance * ytdRate)
                  let totalIncome = ytdIncome + remainingIncome
                  if (remainingIncome > 0 || ytdIncome > 0) {
                    const incomeDetail = thisYear.incomeDetails.find((i: IIncomeDetail) => i.id === asset.id && i.incomeType === IncomeType.NonqualifiedDividends)
                    if (incomeDetail) {
                      incomeDetail.amount += ytdIncome + remainingIncome
                      incomeDetail.remaining += remainingIncome
                    } else {
                      thisYear.incomeDetails.push({
                        id: asset.id,
                        description: `${asset.description} non-qualified dividends (${numberToPercentFormat( effectiveRate, 2)})`,
                        incomeType: IncomeType.NonqualifiedDividends,
                        amount: ytdIncome + remainingIncome,
                        remaining: remainingIncome
                      })
                    }
                    thisYear.grossIncome += ytdIncome + remainingIncome

                    thisYear.deductions += totalIncome
                    thisYear.remainingDeductions += remainingIncome
                    thisYear.contributions += totalIncome
                    ad.contribution += remainingIncome
                    ad.balance += remainingIncome
                  }
                }

                const capitalGrowth = this.getAssetIncomeDetail(asset, IncomeType.LongTermCapitalGainsOrLoss)
                if (capitalGrowth && capitalGrowth.rate) {
                  const effectiveRate = capitalGrowth.rate * rateOverrideFactor
                  let remainingRate = prevYear ? effectiveRate : effectiveRate * proRateFactor
                  let ytdRate = prevYear ? 0 : effectiveRate - remainingRate
                  let remainingIncome = roundMoney(balance * remainingRate)
                  let ytdIncome = roundMoney(boyBalance * ytdRate)

                  ad.balance += remainingIncome
                }
              } else {
                // Handle regular taxable interest income
                let remainingRate = prevYear ? growthRate : growthRate * proRateFactor
                let ytdRate = prevYear ? 0 : growthRate - remainingRate
                let remainingIncome = roundMoney(balance * remainingRate)
                let ytdIncome = roundMoney(boyBalance * ytdRate)
                let totalIncome = ytdIncome + remainingIncome

                if (remainingIncome > 0 || ytdIncome > 0) {
                  const incomeDetail = thisYear.incomeDetails.find((i: IIncomeDetail) => i.id === asset.id && i.incomeType === IncomeType.TaxableInterest)
                  if (incomeDetail) {
                    incomeDetail.amount += ytdIncome + remainingIncome
                    incomeDetail.remaining += remainingIncome
                  } else {
                    thisYear.incomeDetails.push({
                      id: asset.id,
                      description: `${asset.description} interest (${numberToPercentFormat( growthRate, 2)})`,
                      incomeType: IncomeType.TaxableInterest,
                      amount: ytdIncome + remainingIncome,
                      remaining: remainingIncome
                    })
                  }
                  thisYear.grossIncome += ytdIncome + remainingIncome
                  thisYear.deductions += totalIncome
                  thisYear.remainingDeductions += remainingIncome
                  thisYear.contributions += totalIncome
                  ad.contribution += remainingIncome
                  ad.balance += remainingIncome
                }
              }
            }
          } else {
            let startDate = ad.liquidAsset.startDate
            if (startDate && startDate.getFullYear() <= thisYear.year) {
              if (prevYear) {
                ad.balance = roundMoney(ad.balance * (1 + ad.growthRate))
              } else {
                // Base growth on current balance for first year
                let growthRate = ad.growthRate * this.proRateGrowthFactor(yearNum, startDate, thisYear.year, startAt)
                ad.growthRate = growthRate
                ad.balance = roundMoney(ad.balance * (1 + growthRate))
              }
            } else {
              ad.balance = ad.boyBalance
            }
          }
        })
      } else {
        // Compute asset growth (simple version)
        thisYear.assetDetails.forEach((ad: IAssetDetail) => {
          // Determine growth
          let startDate = ad.liquidAsset.startDate
          if (startDate && startDate.getFullYear() <= thisYear.year) {
            if (prevYear) {
              ad.balance = roundMoney(ad.balance * (1 + ad.growthRate))
            } else {
              // Base growth on current balance for first year
              let growthRate = ad.growthRate * this.proRateGrowthFactor(yearNum, startDate, thisYear.year, startAt)
              ad.growthRate = growthRate
              ad.balance = roundMoney(ad.balance * (1 + growthRate))
            }
          } else {
            ad.balance = ad.boyBalance
          }
        })
      }

      // Compute real estate asset growth
      thisYear.realDetails.forEach((rd: IRealAssetDetail) => {
        rd.balance = rd.balance * (1 + rd.growthRate)
      })

      // Apply conversions (if not already applied)
      if (thisYear.conversions.length === 0) {
        this.applyFutureConversions(model, thisYear);
      }

      thisYear.grossIncome = thisYear.incomeDetails.reduce((accum: number, detail: IIncomeDetail)=> accum + detail.amount, 0)
        thisYear.remainingIncome = thisYear.incomeDetails.reduce((accum: number, detail: IIncomeDetail)=> accum + detail.remaining, 0)

        const taxEstimate = this.estimateTaxes(thisYear, model)
        thisYear.taxes = taxEstimate.totalEstimatedTax
        thisYear.budgetCalc.totalEstimatedTaxes = taxEstimate.totalEstimatedTax
        thisYear.taxRate = taxEstimate.effectiveTotalTaxRate
        thisYear.netIncome = Math.round(thisYear.grossIncome - thisYear.deductions - thisYear.taxes)
        thisYear.surplus = Math.round(thisYear.netIncome + thisYear.rmds - thisYear.allExpenses)

        // TODO: Test not calculating remainingSurplus for first year
        if (yearNum === 1 && proRateBudget) {
            thisYear.remainingSurplus = thisYear.remainingIncome - thisYear.remainingDeductions - thisYear.taxes +
              thisYear.remainingRMDs - thisYear.remainingExpenses + thisYear.remainingDistributions

            if (thisYear.remainingSurplus < 0) {
                const proRateSurplus = (thisYear.grossIncome - thisYear.remainingIncome) - (thisYear.deductions - thisYear.remainingDeductions) - (thisYear.allExpenses - thisYear.remainingExpenses)
                const diff = proRateSurplus + thisYear.remainingSurplus
                if (diff > 0) {
                    thisYear.remainingSurplus = diff
                }
            }
        } else {
            thisYear.remainingSurplus = Math.round(thisYear.surplus + thisYear.expenseDistributions + thisYear.remainingDistributions)
        }

        // New Recursive Shortfall handling
        if (depth < 10 && thisYear.remainingSurplus <= -1) {
          // Determine if any assets remain
          const assetsRemaining = thisYear.assetDetails.reduce((accum: number, detail: IAssetDetail) => accum + detail.balance, 0)
          if (assetsRemaining >= 1) {
            let shortfall = -thisYear.remainingSurplus + prevShortfall
            if (depth === 0 && -thisYear.remainingSurplus >= thisYear.taxes) {
              // Avoid overwithdrawing
              shortfall -= thisYear.taxes
            }
            shortfall = Math.round(shortfall)

            // console.log(`${calcYear} depth: ${depth}, prevShortfall: ${prevShortfall}, remainingShortfall: ${-thisYear.remainingSurplus}, shortfall: ${shortfall}, taxes ${thisYear.taxes}`)

            // Recalculate year with distributions
            return this.calculateFutureYear(calcYear, model, futureCalc, depth + 1, shortfall)
          }
        } else if (depth > 0 && depth < 10 && thisYear.remainingSurplus >= 1) {
          // Overshot shortfall, try again + remainingSurplus / 2 (if no taxes) else + remainingSurplus
          let shortfall = thisYear.taxes === 0 ? Math.round(prevShortfall - (thisYear.remainingSurplus / 2)) : Math.round(prevShortfall - thisYear.remainingSurplus)
          // console.log(`${calcYear} depth: ${depth}, prevShortfall: ${prevShortfall}, remainingShortfall: ${-thisYear.remainingSurplus}, shortfall: ${shortfall}, taxes ${thisYear.taxes}`)
          return this.calculateFutureYear(calcYear, model, futureCalc, depth + 1, shortfall)
        }

        // Handle surplus
        const totalSurplus = thisYear.remainingSurplus // + thisYear.remainingDistributions
        if (totalSurplus > 1) {
            if (futureCalc.growthStrategy.surplusAssetId) {
                const surplusAssetId = futureCalc.growthStrategy.surplusAssetId
                const assetDetail = thisYear.assetDetails.find((ad: IAssetDetail) => ad.liquidAsset.id === surplusAssetId)
                if (assetDetail) {
                    const amount = futureCalc.growthStrategy.surplusAssetPercent ?
                      Math.round(totalSurplus * futureCalc.growthStrategy.surplusAssetPercent) : totalSurplus
                    assetDetail.contribution += amount
                    assetDetail.balance += amount
                    assetDetail.yoyChange = assetDetail.liquidAsset.balance !== 0 ? assetDetail.balance / assetDetail.liquidAsset.balance : 0
                    thisYear.contributions += amount
                    // DON'T Reduce surplus to reflect contribution
                    // thisYear.surplus -= amount
                }
            }
        }

        if (yearNum === 1 && proRateBudget) {
            // Check for difference in distributions to cover total shortfall
            if (thisYear.surplus < 0) {
                // const totalDistributions = thisYear.assetDetails.reduce((accum: number, detail: IAssetDetail) => {
                //     return (accum + detail.distribution)
                // }, 0)
                // const unaccounted = thisYear.surplus - totalDistributions
                const unaccounted = thisYear.surplus + thisYear.allDistributions
                // console.log(`unaccounted = ${unaccounted}`)
                if (unaccounted < 0) {
                    thisYear.unaccounted = unaccounted
                }
            }
        }

        // Calculate EOY Balances and summary info
        thisYear.assetDetails.forEach((ad: IAssetDetail) => {
          ad.eoyBalance = ad.balance
          thisYear.netLiquidAssets += ad.eoyBalance
          ad.yoyChange = ad.liquidAsset.balance !== 0 ? ad.eoyBalance / ad.liquidAsset.balance : 0
          ad.withdrawalRate = (ad.distribution - ad.rmd) / ad.boyBalance
          const assetIncome = ad.liquidAsset.assetIncome
          if (assetIncome) {
            if (ad.liquidAsset.assetType === AssetType.Brokerage) {
              const gainOrLoss = ad.contribution + ad.distribution + ad.conversion
              if (gainOrLoss > 0) {
                ad.eoyBasis = ad.boyBasis + gainOrLoss
              } else if (gainOrLoss < 0) {
                const capitalGain = this.calcCapitalGain(ad.boyBalance, ad.boyBasis, -gainOrLoss)
                if (capitalGain) {
                  thisYear.qualifiedCapitalGainsDistributions += capitalGain
                  const returnOfBasis = -gainOrLoss - capitalGain
                  ad.eoyBasis = ad.boyBasis - returnOfBasis
                }
              }
            }
          }
        })

        thisYear.realDetails.forEach((rd: IRealAssetDetail) => {
          rd.eoyBalance = rd.balance
          rd.yoyChange = rd.realAsset.balance !== 0 ? rd.eoyBalance / rd.realAsset.balance : 0
          if (rd.realAsset.assetCategory === AssetCategory.RealEstateAndProperty) {
            thisYear.netRealAssets += rd.balance
          }
        })

        // Calculate yearOverYearChange and withdrawalChange
        if (futureCalc!.futureYears.length > 0) {
            thisYear.yoyChange = futureCalc!.futureYears[0].currentLiquidAssets !== 0 ? thisYear.netLiquidAssets / futureCalc!.futureYears[0].currentLiquidAssets : 0
        } else {
            thisYear.yoyChange = thisYear.currentLiquidAssets !== 0 ? thisYear.netLiquidAssets / thisYear.currentLiquidAssets : 0
        }
        thisYear.withdrawalRate = thisYear.currentLiquidAssets !== 0 ? -thisYear.allDistributions / thisYear.currentLiquidAssets : 0

        // Update benchmarks
        if (yearNum >= futureCalc.benchmarkData.startYear && yearNum <= futureCalc.benchmarkData.endYear) {
            futureCalc.benchmarkData.guaranteedIncome += thisYear.guaranteedIncome
            futureCalc.benchmarkData.essentialExpenses += thisYear.essentialExpenses

            if (yearNum === futureCalc.benchmarkData.startYear) {
                futureCalc.benchmarkData.withdrawalRate = -thisYear.withdrawalRate
            }
        }

        return thisYear
    }

  private applyFutureConversions(model: Model, thisYear: IFutureYear) {
    const conversions = model.assetConversions.filter((c: AssetConversion) => c.year === thisYear.year && (!c.archived || c.completedAt))
    conversions.forEach((conversion: AssetConversion) => {
      // Create a copy
      conversion = new AssetConversion(conversion)
      const convType = conversion.conversionType
      const isQCD = convType === AssetConversionType.QCD

      let source: IAssetDetail | IRealAssetDetail | undefined = thisYear.assetDetails.find((a: IAssetDetail) => a.liquidAsset.id === conversion.srcAssetId)
      if (!source) {
        source = thisYear.realDetails.find((a: IRealAssetDetail) => a.realAsset.id === conversion.srcAssetId)
      }
      let dest: IAssetDetail | IRealAssetDetail | undefined
      if (!isQCD) {
        dest = thisYear.assetDetails.find((a: IAssetDetail) => a.liquidAsset.id === conversion.dstAssetId)
        if (!dest) {
          dest = thisYear.realDetails.find((a: IRealAssetDetail) => a.realAsset.id === conversion.dstAssetId)
        }
      }
      if (source && (isQCD || dest)) {
        thisYear.conversions.push(conversion)
        if (!conversion.completedAt) {
          if (isQCD) {
            const qcd = source as IAssetDetail
            const rmd = qcd.rmd
            let amount = 0
            if (conversion.percent > 0) {
              amount = Math.min(rmd * conversion.percent, AssetConversion.MaxQCD)
            } else {
              amount = Math.min(conversion.amount, AssetConversion.MaxQCD)
            }
            if (rmd > 0) {
              if (amount >= rmd) {
                qcd.conversion -= amount
                qcd.rmd = 0
                qcd.balance -= (amount - rmd)
                thisYear.rmds -= rmd
                thisYear.iraDistributions += (amount - rmd)
                if (qcd.liquidAsset.assetTypeDef.isTaxableIRA) {
                  thisYear.taxableIraDistributions -= rmd
                }
                thisYear.allDistributions += (amount - rmd)
              } else { // amount < rmd
                // TODO: Recheck this logic and not updating balance or allDistributions logic flagged by Claude
                qcd.conversion -= amount
                qcd.rmd -= amount
                thisYear.rmds -= amount
                // thisYear.iraDistributions += amount
                if (qcd.liquidAsset.assetTypeDef.isTaxableIRA) {
                  thisYear.taxableIraDistributions -= amount
                }
              }
            } else {
              qcd.conversion -= amount
              thisYear.iraDistributions += amount
              qcd.balance -= amount
              thisYear.allDistributions += amount
            }
          } else {
            const amount = conversion.amount > 0 ? Math.min(conversion.amount, source.balance) :
              Math.min(source.balance * conversion.percent, source.balance)
            conversion.amount = amount
            source.conversion -= amount
            const balance = source.balance
            source.balance -= amount
            thisYear.allDistributions += amount
            if ("liquidAsset" in source) {
              const liquidAsset = source.liquidAsset
              if (conversion.incomeType === IncomeType.TaxableIRADistribution) {
                thisYear.iraDistributions += amount
                thisYear.taxableIraDistributions += Math.max(amount - conversion.nonTaxableAmount, 0)
              } else if (conversion.incomeType === IncomeType.LongTermCapitalGainsOrLoss) {
                // This is handled at the end
              }
            } else if ("realAsset" in source) {
              source.eoyBalance -= amount
            }
            if (dest) {
              dest.conversion += amount
              dest.balance += amount
            }
          }
        } else if (dest) {
          const amount = conversion.amount
          source.conversion -= amount
          // source.balance -= amount
          thisYear.allDistributions += amount
          if ("liquidAsset" in source && conversion.incomeType === IncomeType.TaxableIRADistribution) {
            thisYear.iraDistributions += amount
            thisYear.taxableIraDistributions += Math.max(amount - conversion.nonTaxableAmount, 0)
          }
          dest.conversion += amount
          // dest.balance += amount
        }
      }
    })
  }

  proRateGrowthFactor(yearNum: number, startDate: Date, thisYear : number, balanceDate: Date) {
      if (yearNum === 1 || startDate.getFullYear() === thisYear) {
        const dayOfYear = getDayOfYear(balanceDate)
        const daysInYear = getDaysInYear(balanceDate)
        return (daysInYear - dayOfYear + 1) / daysInYear
      } else {
        return 1
      }
    }

    getAssetIncomeDetail(asset: Asset, incomeType: IncomeType) : AssetIncomeDetail | null | undefined {
      if (asset.assetType === AssetType.Brokerage &&
        asset.assetIncome && asset.assetIncome.details) {
        return asset.assetIncome.details.find((d: AssetIncomeDetail | null | undefined) => d && d.incomeType === incomeType)
      } else {
        return undefined
      }
    }

  calcCapitalGain(balance: number, basis: number, distribution: number) : number {
    const unrealizedGain = balance - basis
    if (unrealizedGain > 0) {
      const gainRatio = unrealizedGain / balance
      return(roundMoney(distribution * gainRatio))
    } else {
      return 0
    }
  }

    // Group AssetDetails by withdrawalOrder
    groupAssetDetails(details: IAssetDetail[]) {
        const groups: IAssetDetailGroup[] = []
        const otherGroups: IAssetDetailGroup[] = []
        details.forEach((detail: IAssetDetail) => {
            if (detail.liquidAsset.withdrawalOrder > 0) {
                const group = groups.find((g: IAssetDetailGroup) => g.withdrawalOrder === detail.liquidAsset.withdrawalOrder)
                if (group) {
                    group.details.push(detail)
                    group.balance += detail.balance
                } else {
                    groups.push({
                        withdrawalOrder: detail.liquidAsset.withdrawalOrder,
                        balance: detail.balance,
                        details: [detail]
                    })
                }
            } else {
                const otherGroup = otherGroups.find((g: IAssetDetailGroup) => g.withdrawalOrder === detail.liquidAsset.assetTypeDef.withdrawalOrder)
                if (otherGroup) {
                    otherGroup.details.push(detail)
                    otherGroup.balance += detail.balance
                } else {
                    otherGroups.push({
                        withdrawalOrder: detail.liquidAsset.assetTypeDef.withdrawalOrder,
                        balance: detail.balance,
                        details: [detail]
                    })
                }
            }
        })
        groups.sort((a: IAssetDetailGroup, b: IAssetDetailGroup) => a.withdrawalOrder - b.withdrawalOrder)
        let baseWithdrawalOrder = groups.length > 0 ? groups[groups.length - 1].withdrawalOrder + 1 : 1
        otherGroups.sort((a: IAssetDetailGroup, b: IAssetDetailGroup) => a.withdrawalOrder - b.withdrawalOrder)
        otherGroups.forEach((g: IAssetDetailGroup, index: number) => {
            g.withdrawalOrder = baseWithdrawalOrder + index
        })
        return [...groups, ...otherGroups]
    }

    getCurrentAssetBalance(year: number, asset: Asset, snapshot?: Snapshot) : number {
      let balance = 0
      // Skip assets with a future start date
      if (asset.startDate && asset.startDate.getFullYear() <= year) {
        balance = asset.balance
      }

      return balance
    }

    getBoYAssetBalance(year: number, asset: Asset, snapshot?: Snapshot) {
        let balance = 0

        if (asset.priorEoYBalance && getISOYear(asset.updatedAt) === year) {
          balance = asset.priorEoYBalance
        } else {
          const startDate = asset.startDate
          if (startDate && startDate.getFullYear() <= year) {
            if (snapshot) {
              const detail = snapshot.getSnapshotDetail(asset)
              if (detail) {
                if (snapshot.date.getFullYear() === year) {
                  // Back calculate the BoY balance
                  const dayOfYear = getDayOfYear(snapshot.date)
                  const daysInYear = getDaysInYear(snapshot.date)
                  const rate = 1 + (asset.returnRate * (dayOfYear - 1) / daysInYear)
                  balance = roundMoney((detail.balance + asset.withdrawalsYTD) / rate)
                }
              }
            }
            if (!balance && asset.balanceDate && asset.balanceDate.getFullYear() <= year) {
              if (getISOYear(asset.updatedAt) === year) {
                // Back calculate the BoY balance
                const dayOfYear = getDayOfYear(asset.balanceDate)
                const daysInYear = getDaysInYear(asset.balanceDate)
                const rate = 1 + (asset.returnRate * (dayOfYear - 1) / daysInYear)
                balance = roundMoney((asset.balance + asset.withdrawalsYTD) / rate)
              } else {
                balance = asset.balance
              }
            }
          }
        }

        return balance
    }

    getBoYLiabilityBalance(year: number, liability: Liability, snapshots: Snapshot[]) {
        const startDate = liability.startDate
        if (startDate && startDate.getFullYear() <= year) {
            // Find earliest snapshot for the year
            // let found: ISnapshotDetail | undefined
            // for (let i = 0; i < snapshots.length && !found; i++) {
            //     const snapshot = snapshots[i]
            //     const snapshotYear = snapshot.date.getFullYear()
            //     if (snapshotYear === year) {
            //         found = snapshot.getSnapshotDetail(asset)
            //     }
            // }
            // if (found) {
            //     return found.balance
            // } else {
            if (liability.balanceDate && liability.balanceDate.getFullYear() <= year) {
                return liability.balance
            }
            // }
        }
        return 0
    }

    getLiabilityExpense(model: Model, liabilityId: string) {
        return model.expenses.find((e: Expense) => e.liabilityId === liabilityId)
    }

    getLiabilityPaymentTotal(budgetCalc: IBudgetCalc, id: string): number {
        let total = budgetCalc.details.reduce((accum: number, item: IBudgetItemDetail) => item.id === id ? accum + item.amount : accum, 0)
        return total
    }

    rmdDistributionPeriod(yearsPast: number, liquidAsset: LiquidAsset) {
        // TODO: Consider a distribution function for each asset type
        const assetTypeDef = liquidAsset.assetTypeDef

        const ownerAgeCurrent = liquidAsset.ownerAge + yearsPast
        const spouseAgeCurrent = liquidAsset.spouseAge + yearsPast

        if (assetTypeDef.use10YDist) {
            const distYearsLeft = liquidAsset.distYearsLeft - yearsPast
            return distYearsLeft > 0 ? distYearsLeft : 1
        }

        if (assetTypeDef.useTable1Once) {
            return liquidAsset.distPeriodStart - yearsPast
        }

        if (ownerAgeCurrent < 72) {
            return TABLE1_1D[ownerAgeCurrent > 120 ? 120 : ownerAgeCurrent]
        }

        if (assetTypeDef.considerSpouseTable && liquidAsset.spouseAge && (liquidAsset.ownerAge - liquidAsset.spouseAge >= 10)) {
            const [i, j] = [Math.max(ownerAgeCurrent, spouseAgeCurrent), Math.min(ownerAgeCurrent, spouseAgeCurrent)]
            return TABLE2_2D[Math.min(i, 120) - 0][Math.min(j, 120) - 0]
        }
        return TABLE3_1D[(ownerAgeCurrent > 115 ? 115 : ownerAgeCurrent) - 72]
    }

    // Order assets by withdrawal order using custom order followed by default
    orderLiquidAssets(liquidAssets: LiquidAsset[]) {
        if (liquidAssets.length > 1) {
            // Sort first by any existing withDrawalOrder
            liquidAssets.sort((a: LiquidAsset, b: LiquidAsset) => a.order - b.order)
            // Order those with 0 order according to the default ordering + max of any custom ordered items
            const max = liquidAssets[liquidAssets.length - 1].order
            liquidAssets.forEach((a: LiquidAsset) => {
                if (a.order === 0) {
                    a.order = a.assetTypeDef.withdrawalOrder + max
                }
            })
            // Resort
            liquidAssets.sort((a: LiquidAsset, b: LiquidAsset) => a.order - b.order)
        }
    }

    orderAssets(assets: Asset[]) {
        if (assets.length > 1) {
            // Sort first by any existing withDrawalOrder
            assets.sort((a: Asset, b: Asset) => a.withdrawalOrder - b.withdrawalOrder)
            // Order those with 0 order according to the default ordering + max of any custom ordered items
            const max = assets[assets.length - 1].withdrawalOrder
            assets.forEach((a: Asset) => {
                if (a.withdrawalOrder === 0) {
                    a.withdrawalOrder = a.assetTypeDef.withdrawalOrder + max
                }
            })
            // Resort
            assets.sort((a: Asset, b: Asset) => a.withdrawalOrder - b.withdrawalOrder)
        }
    }

    // Determine what amount of asset income is to be reinvested in a given year
    calculateAssetIncomeReinvestment(income: number, year: number, date: Date) {
      // If date is greater than the current year, no pro-rating is needed
      if (date.getFullYear() > year) {
        return income
      } else if (date.getFullYear() < year) {
        return 0
      } else { // Pro-rate the income for current year
        const percent = getDayOfYear(date) / getDaysInYear(date)
        return Math.round(income * percent)
      }
    }

  // Determine what amount of asset income is to be reinvested in a given year
  calculateAssetIncomeRate(incomeRate: number, year: number, date: Date) {
    // If date is greater than the current year, no pro-rating is needed
    if (date.getFullYear() > year) {
      return incomeRate
    } else if (date.getFullYear() < year) {
      return 0
    } else { // Pro-rate for current year
      const percent = getDayOfYear(date) / getDaysInYear(date)
      return incomeRate * percent
    }
  }

  /*
  calculateReinvestPercent(year: number, assetIncomeDate: Date) {
    // If date is greater than the current year, then 100%
    if (assetIncomeDate.getFullYear() > year) {
      return 1
    } else if (assetIncomeDate.getFullYear() < year) {
      // date < year, then 0%
      return 0
    } else { // Pro-rate for current year
      const percent = getDayOfYear(assetIncomeDate) / getDaysInYear(assetIncomeDate)
      return percent
    }
  }
  */

  estimateTaxes(futureYear: IFutureYear, model: Model) {
        const year = futureYear.year
        const incomeSummary = futureYear.budgetCalc.incomeSummary
        const taxSummary = futureYear.budgetCalc.taxSummary
        const persons = model.persons
        const taxValues = model.taxValues
        const numberOfFilersOver65 = this.getNumberOfFilersOver65(year, persons)

        const input = new TaxInput()
        input.filingStatus = this.getTaxValue(year, "filingStatus", taxValues, persons.length > 1 ? TaxFilingStatus.MarriedFilingJointly : TaxFilingStatus.Single)
        if (input.filingStatus === TaxFilingStatus.MarriedFilingJointly && futureYear.expectedMaritalStatus === MaritalStatus.Single) {
            // Override for death of spouse
            input.filingStatus = TaxFilingStatus.Single
        }
        input.taxYear = year
        input.numberOfFilersOver65 = numberOfFilersOver65
        input.income.wagesSalariesTips = this.getIncomeTypeTotal(IncomeType.WagesSalariesTips, incomeSummary)
        input.income.taxExemptInterest = this.getIncomeTypeTotal(IncomeType.TaxExemptInterest, incomeSummary)
        // input.income.taxableInterest = this.getIncomeTypeTotal(IncomeType.TaxableInterest, incomeSummary)
        // input.income.qualifiedDividends = this.getIncomeTypeTotal(IncomeType.QualifiedDividends, incomeSummary)
        // input.income.unqualifiedDividends = this.getIncomeTypeTotal(IncomeType.NonqualifiedDividends, incomeSummary)
        input.income.taxableInterest = this.getIncomeDetailTotal(IncomeType.TaxableInterest, futureYear.incomeDetails)
        input.income.qualifiedDividends = this.getIncomeDetailTotal(IncomeType.QualifiedDividends, futureYear.incomeDetails)
        input.income.unqualifiedDividends = this.getIncomeDetailTotal(IncomeType.NonqualifiedDividends, futureYear.incomeDetails)
        input.income.ordinaryDividends = input.income.qualifiedDividends + input.income.unqualifiedDividends
        input.income.totalIraDistributions = futureYear.iraDistributions
        input.income.taxableIraDistributions = futureYear.taxableIraDistributions
        input.income.taxablePensionAndAnnuities = this.getIncomeTypeTotal(IncomeType.PensionsAndAnnuitiesTaxable, incomeSummary) + futureYear.unqualifiedDistributions
        input.income.nonTaxablePensionAndAnnuities = this.getIncomeTypeTotal(IncomeType.PensionsAndAnnuitiesNonTaxable, incomeSummary)
        input.income.totalPensionsAndAnnuities = input.income.taxablePensionAndAnnuities + input.income.nonTaxablePensionAndAnnuities
        input.income.totalSocialSecurityBenefits = this.getIncomeTypeTotal(IncomeType.SocialSecurityBenefits, incomeSummary)
        input.income.qualifiedCapitalGains = this.getIncomeTypeTotal(IncomeType.LongTermCapitalGainsOrLoss, incomeSummary) + futureYear.qualifiedCapitalGainsDistributions
        input.income.unqualifiedCapitalGains = this.getIncomeTypeTotal(IncomeType.ShortTermCapitalGainsOrLoss, incomeSummary)
        input.income.capitalGainOrLoss = input.income.qualifiedCapitalGains + input.income.unqualifiedCapitalGains
        input.income.otherIncome = this.getIncomeTypeTotal(IncomeType.OtherIncome, incomeSummary)
        // Compute NIIT-eligible Other Income (all OtherIncome items not excluded)
        input.income.otherIncomeForNiit = incomeSummary.byItem
            .filter((item: IIncomeItemSummary) => item.incomeType === IncomeType.OtherIncome && !item.excludeFromNIIT)
            .reduce((sum: number, item: IIncomeItemSummary) => sum + item.total, 0)
        // Handle AssetConversions
        futureYear.conversions.forEach((conversion: AssetConversion) => {
            if (conversion.incomeType) {
                const income = Math.max(conversion.amount - conversion.nonTaxableAmount, 0)
                switch (conversion.incomeType) {
                    case IncomeType.WagesSalariesTips:
                        input.income.wagesSalariesTips += income
                        break
                    case IncomeType.LongTermCapitalGainsOrLoss:
                        input.income.qualifiedCapitalGains += income
                        break
                    case IncomeType.ShortTermCapitalGainsOrLoss:
                        input.income.unqualifiedCapitalGains += income
                        break
                    case IncomeType.OtherIncome:
                        input.income.otherIncome += income
                        if (conversion.conversionType !== AssetConversionType.RothConversion) {
                            input.income.otherIncomeForNiit += income
                        }
                        break
                }
            }
        })
        input.income.adjustmentsToIncome = this.getTaxValue(year, "adjustmentsToIncome", taxValues)
        input.itemizedDeductionsIn = this.getTaxValue(year, "itemizedDeductionsIn", taxValues)
        input.otherQualifyingDeductionsToTaxableIncome = this.getTaxValue(year, "otherQualifyingDeductionsToTaxableIncome", taxValues)
        input.childTaxCreditOrCreditForOtherDependents = this.getTaxValue(year, "childTaxCreditOrCreditForOtherDependents", taxValues)
        input.additionalCreditsAndPayments = this.getTaxValue(year, "additionalCreditsAndPayments", taxValues)
        input.otherTaxes = this.getTaxTypeTotal(TaxType.OtherTax, taxSummary)
        input.federalIncomeTaxWithheld = this.getTaxTypeTotal(TaxType.FederalIncomeTax, taxSummary)
        input.earnedIncomeCredit = this.getTaxValue(year, "earnedIncomeCredit", taxValues)
        input.refundableAdditionalChildTaxCredits = this.getTaxValue(year, "refundableAdditionalChildTaxCredits", taxValues)
        input.otherPaymentsAndRefundableCredits = this.getTaxValue(year, "otherPaymentsAndRefundableCredits", taxValues)
        input.socialSecurityTaxWithheld = this.getTaxTypeTotal(TaxType.SocialSecurityTax, taxSummary)
        input.medicareTaxWithheld = this.getTaxTypeTotal(TaxType.MedicareTax, taxSummary)
        input.stateIncomeTaxWithheld = this.getTaxTypeTotal(TaxType.StateIncomeTax, taxSummary)
        input.localIncomeTaxWithheld = this.getTaxTypeTotal(TaxType.LocalIncomeTax, taxSummary)
        input.inflationRate = model.inflationStrategy ? model.inflationStrategy.tax.rate : 0
        const stateNumber = this.getTaxValue(year, "stateTaxState", taxValues)
        if (stateNumber >= 0) {
            input.stateTaxState = stateNumberToCode(stateNumber)
        }
        let stateRate = this.getTaxValue(year, "stateTaxRate", taxValues)
        if (stateRate >= 0) {
            input.stateTaxRate = stateRate / 100
        }
        // Additional deductions
        input.additionalDeductions.autoLoanInterest = this.getTaxValue(year, "autoLoanInterest", taxValues)
        input.additionalDeductions.charitableContributions = this.getTaxValue(year, "charitableContributions", taxValues)
        input.additionalDeductions.overtimeIncome = this.getTaxValue(year, "overtimeIncome", taxValues)
        input.additionalDeductions.tipIncome = this.getTaxValue(year, "tipIncome", taxValues)

        futureYear.taxInput = input

        futureYear.taxEstimate = this.taxEstimator.estimateTax(input)
        return futureYear.taxEstimate
    }

    getTaxValue = (year: number, key: TaxValueKey, taxValues: TaxValue[], defaultValue?: number): number => {
        let value = 0
        let taxValue = this.getTaxValueObj(year, key, taxValues)
        if (taxValue === undefined) {
            value = defaultValue !== undefined ? defaultValue : 0
        } else if (taxValue.value !== null) {
            value = taxValue.value
        }
        return value
    }

    getTaxValueObj(year: number, key: TaxValueKey, taxValues: TaxValue[]) {
        // Find the matching TaxValue closest, but less then the currentYear
        let found: TaxValue | undefined
        let index = 0
        while (index < taxValues.length && taxValues[index].year <= year) {
            if (taxValues[index].key === key) {
                found = taxValues[index]
            }
            ++index
        }

        return found
    }

    getNumberOfFilersOver65(year: number, persons: Person[]) {
        let count = 0
        const taxDate = new Date(year, 11, 31)
        if (persons.length > 0 && persons[0]!.birthDate) {
            const taxAge = differenceInYears(taxDate, isoToLocalDate(persons[0]!.birthDate))
            if (taxAge >= 65) {
                ++count
            }
        }
        if (persons.length > 1 && persons[1]!.birthDate) {
            const taxAge = differenceInYears(taxDate, isoToLocalDate(persons[1]!.birthDate))
            if (taxAge >= 65) {
                ++count
            }
        }
        return count
    }

    getIncomeTypeTotal = (incomeType: IncomeType, incomeSummary: IIncomeSummary): number => {
        let total = 0
        if (incomeSummary) {
            const incomeTypeSummary = incomeSummary.byType.find((typeSummary: IIncomeTypeSummary) => typeSummary.incomeType === incomeType)
            if (incomeTypeSummary) {
                total = incomeTypeSummary.total
            }
        }
        return total
    }

    getIncomeDetail = (id: string, incomeType: IncomeType, incomeDetails: IIncomeDetail[]) => {
      return incomeDetails.find((incomeDetail: IIncomeDetail) => incomeDetail.id === id && incomeDetail.incomeType === incomeType)
    }

    getIncomeDetailTotal = (incomeType: IncomeType, incomeDetails: IIncomeDetail[]) => {
      const total = incomeDetails.reduce((accum: number, detail: IIncomeDetail) => {
        return (detail.incomeType === incomeType ? accum + detail.amount : accum)
      }, 0)
      return total
    }

    getTaxTypeTotal = (taxType: TaxType, taxSummary: ITaxSummary): number => {
        let total = 0
        if (taxSummary) {
            const taxTypeSummary = taxSummary.byType.find((typeSummary: ITaxTypeSummary) => typeSummary.taxType === taxType)
            if (taxTypeSummary) {
                total = taxTypeSummary.total
            }
        }
        return total
    }

    enableOccurencesCache() {
      this.occurencesCache = new Map<string, any[]>([])
    }

    disableOccurencesCache() {
      this.occurencesCache = undefined
    }

    async calculateMonteCarlo(model: Model, plan: Plan, maxEndYear: number) {
        this.budgetCache = new Map<string, IBudgetCalc>([])
        this.enableOccurencesCache()
        const baseCalc = this.calculateFuture(model, plan.name, maxEndYear)

        let rateModel = model.getSetting(ModelSetting.MonteCarloRateModel, undefined)
        if (!rateModel) {
            switch (model.growthStrategy?.growthModel) {
                case GrowthModel.Moderate:
                    rateModel = GrowthModel.Moderate
                    break
                case GrowthModel.Balanced:
                    rateModel = GrowthModel.Balanced
                    break
                case GrowthModel.Aggressive:
                    rateModel = GrowthModel.Aggressive
                    break
                default:
                    rateModel = GrowthModel.Moderate
                    break
            }
        }
        let simulations = model.getSetting(ModelSetting.MonteCarloSimulations, "1000")
        let count = parseInt(simulations) ?? 1000
        const startYear = new Date().getFullYear()
        const years = maxEndYear - startYear + 1
        const age1 = model.person1?.getAge() ?? 0
        const age2 = model.person2?.getAge() ?? 0

        const growthStrategy = new GrowthStrategy(model.growthStrategy)
        growthStrategy.growthModel = GrowthModel.Custom
        growthStrategy.returnSequence = ReturnSequence.Historical
        growthStrategy.ignoreCustomGrowthRates = true

        // const inflationStrategy = new InflationStrategy(model.inflationStrategy)
        // inflationStrategy.expense.lock = true

        const calcs: FutureCalc[] = []

        for (let i = 0; i < count; i++) {
            const {returnRates} = this.generateMonteCarloRateSequence(rateModel, years)
            growthStrategy.customModel = returnRates
            baseCalc.growthStrategy = growthStrategy
            // inflationStrategy.expense.custom = inflationRates
            // baseCalc.inflationStrategy = inflationStrategy
            calcs.push(this.recalculateFuture(baseCalc, `${i}`))
        }

        const percentiles = [0.10, 0.25, 0.5, 0.75, 0.9]
        const finalPercentiles: number[] = range(0, 100, 1).map((p: number) => p / 100)
        let finalBalances: number[] = []

        // Build Results
        const result: IMonteCarloResults = {
            updatedAt: model.updatedAt,
            rateModel: rateModel,
            simulations: count,
            labels: ["10th Percentile", "25th Percentile", "50th Percentile", "75th Percentile", "90th Percentile", baseCalc.label],
            percentiles: [0, ...percentiles],
            years: [],
            confidenceScore: 0,
            scenarioPercentile: 1
        }

        for (let year = 0; year < years; year++) {
            const dist = new Array<number>(count)
            calcs.forEach((calc: FutureCalc, index: number) => {
                dist[index] = calc.futureYears[year].netLiquidAssets
            })
            const baseBalance = baseCalc.futureYears[year].netLiquidAssets
            const balances = [...StatCalc.calculatePercentiles(dist, percentiles), baseBalance]
            result.years.push({
                year: year + startYear,
                age1: age1 > 0 ? age1 + year : 0,
                age2: age2 > 0 ? age2 + year : 0,
                balances: balances
            })

            if (year === years - 1) {
                // Get all percentile balances for last year
                finalBalances = [...StatCalc.calculatePercentiles(dist, finalPercentiles), baseBalance]
            }
        }

        this.budgetCache = undefined
        this.disableOccurencesCache()

        // Compute confidenceScore
        for (let i = 0; i < (finalBalances.length - 2) && result.confidenceScore === 0; i++) {
            if (finalBalances[i] > 0) {
                result.confidenceScore = (100 - i) / (finalBalances.length - 1)
            }
        }

        // Compute scenarioScore
        const baseNetLiquidAssets = baseCalc.netLiquidAssets
        for (let i = 0; i < (finalBalances.length - 2) && result.scenarioPercentile === 1; i++) {
            if (baseNetLiquidAssets === 0 || baseNetLiquidAssets < finalBalances[i]) {
                result.scenarioPercentile = i / (finalBalances.length - 1)
            }
        }
        result.labels[result.labels.length - 1] = `${baseCalc.label} (${formatPercentile(result.scenarioPercentile)} Percentile)`

        return result
    }

    generateMonteCarloRateSequence = (model: RateModel, years: number) => {
        const returnRates: Array<number> = Array(years).fill(0)
        const inflationRates : Array<number> = Array(years).fill(0)

        const rateSequence = this.rateModels.getRateSequence(ReturnSequence.Historical, model)
        const inflationSequence = this.rateModels.getRateSequence(ReturnSequence.Historical, RateModel.Inflation)

        for (let j = 0; j < years; j++) {
            const index = Math.floor(Math.random() * (rateSequence.length - 1))
            returnRates[j] = rateSequence[index]
            inflationRates[j] = inflationSequence[index]
            // rateModel[j] = ComplexGrowthTable[index][growthModel][ReturnSequence.Historical]
        }
        return {returnRates, inflationRates}
    }
}

// Table of historical data
//     const ComplexGrowthTable = { "0": { "Aggressive": { "Historical": 0.0445, "BadTiming": -0.0213 }, "Balanced": { "Historical": 0.0398, "BadTiming": 0.0018 }, "Moderate": { "Historical": 0.0351, "BadTiming": 0.0249 } }, "1": { "Aggressive": { "Historical": 0.1424, "BadTiming": -0.0615 }, "Balanced": { "Historical": 0.1297, "BadTiming": -0.0419 }, "Moderate": { "Historical": 0.117, "BadTiming": -0.0224 } }, "2": { "Aggressive": { "Historical": 0.2321, "BadTiming": -0.1126 }, "Balanced": { "Historical": 0.203, "BadTiming": -0.0763 }, "Moderate": { "Historical": 0.174, "BadTiming": -0.04 } }, "3": { "Aggressive": { "Historical": -0.0082, "BadTiming": 0.2066 }, "Balanced": { "Historical": 0.0064, "BadTiming": 0.1805 }, "Moderate": { "Historical": 0.021, "BadTiming": 0.1543 } }, "4": { "Aggressive": { "Historical": 0.2402, "BadTiming": 0.0854 }, "Balanced": { "Historical": 0.2504, "BadTiming": 0.0781 }, "Moderate": { "Historical": 0.2605, "BadTiming": 0.0707 } }, "5": { "Aggressive": { "Historical": 0.1647, "BadTiming": 0.0404 }, "Balanced": { "Historical": 0.1486, "BadTiming": 0.0379 }, "Moderate": { "Historical": 0.1326, "BadTiming": 0.0354 } }, "6": { "Aggressive": { "Historical": 0.0885, "BadTiming": 0.1189 }, "Balanced": { "Historical": 0.0973, "BadTiming": 0.1064 }, "Moderate": { "Historical": 0.1061, "BadTiming": 0.0939 } }, "7": { "Aggressive": { "Historical": 0.2853, "BadTiming": 0.0676 }, "Balanced": { "Historical": 0.2764, "BadTiming": 0.0722 }, "Moderate": { "Historical": 0.2674, "BadTiming": 0.0768 } }, "8": { "Aggressive": { "Historical": 0.1717, "BadTiming": -0.2192 }, "Balanced": { "Historical": 0.1688, "BadTiming": -0.1688 }, "Moderate": { "Historical": 0.1658, "BadTiming": -0.1185 } }, "9": { "Aggressive": { "Historical": 0.0376, "BadTiming": 0.1803 }, "Balanced": { "Historical": 0.0345, "BadTiming": 0.1521 }, "Moderate": { "Historical": 0.0313, "BadTiming": 0.124 } }, "10": { "Aggressive": { "Historical": 0.1293, "BadTiming": 0.1264 }, "Balanced": { "Historical": 0.1183, "BadTiming": 0.1189 }, "Moderate": { "Historical": 0.1074, "BadTiming": 0.1113 } }, "11": { "Aggressive": { "Historical": 0.2631, "BadTiming": 0.0431 }, "Balanced": { "Historical": 0.2463, "BadTiming": 0.051 }, "Moderate": { "Historical": 0.2294, "BadTiming": 0.0588 } }, "12": { "Aggressive": { "Historical": 0.0051, "BadTiming": 0.1188 }, "Balanced": { "Historical": 0.0179, "BadTiming": 0.1056 }, "Moderate": { "Historical": 0.0307, "BadTiming": 0.0925 } }, "13": { "Aggressive": { "Historical": 0.2595, "BadTiming": 0.216 }, "Balanced": { "Historical": 0.2452, "BadTiming": 0.1807 }, "Moderate": { "Historical": 0.2309, "BadTiming": 0.1454 } }, "14": { "Aggressive": { "Historical": 0.0753, "BadTiming": 0.1075 }, "Balanced": { "Historical": 0.0757, "BadTiming": 0.0983 }, "Moderate": { "Historical": 0.076, "BadTiming": 0.0891 } }, "15": { "Aggressive": { "Historical": 0.1035, "BadTiming": 0.0133 }, "Balanced": { "Historical": 0.1051, "BadTiming": 0.0135 }, "Moderate": { "Historical": 0.1066, "BadTiming": 0.0138 } }, "16": { "Aggressive": { "Historical": -0.0047, "BadTiming": 0.0863 }, "Balanced": { "Historical": -0.0102, "BadTiming": 0.0756 }, "Moderate": { "Historical": -0.0158, "BadTiming": 0.065 } }, "17": { "Aggressive": { "Historical": 0.3234, "BadTiming": 0.1564 }, "Balanced": { "Historical": 0.3064, "BadTiming": 0.1363 }, "Moderate": { "Historical": 0.2894, "BadTiming": 0.1162 } }, "18": { "Aggressive": { "Historical": 0.1659, "BadTiming": -0.0287 }, "Balanced": { "Historical": 0.1449, "BadTiming": -0.0232 }, "Moderate": { "Historical": 0.124, "BadTiming": -0.0176 } }, "19": { "Aggressive": { "Historical": 0.2592, "BadTiming": 0.0445 }, "Balanced": { "Historical": 0.235, "BadTiming": 0.0398 }, "Moderate": { "Historical": 0.2108, "BadTiming": 0.0351 } }, "20": { "Aggressive": { "Historical": 0.2321, "BadTiming": 0.1424 }, "Balanced": { "Historical": 0.2141, "BadTiming": 0.1297 }, "Moderate": { "Historical": 0.1961, "BadTiming": 0.117 } }, "21": { "Aggressive": { "Historical": 0.1369, "BadTiming": 0.2321 }, "Balanced": { "Historical": 0.1123, "BadTiming": 0.203 }, "Moderate": { "Historical": 0.0877, "BadTiming": 0.174 } }, "22": { "Aggressive": { "Historical": -0.0213, "BadTiming": -0.0082 }, "Balanced": { "Historical": 0.0018, "BadTiming": 0.0064 }, "Moderate": { "Historical": 0.0249, "BadTiming": 0.021 } }, "23": { "Aggressive": { "Historical": -0.0615, "BadTiming": 0.2402 }, "Balanced": { "Historical": -0.0419, "BadTiming": 0.2504 }, "Moderate": { "Historical": -0.0224, "BadTiming": 0.2605 } }, "24": { "Aggressive": { "Historical": -0.1126, "BadTiming": 0.1647 }, "Balanced": { "Historical": -0.0763, "BadTiming": 0.1486 }, "Moderate": { "Historical": -0.04, "BadTiming": 0.1326 } }, "25": { "Aggressive": { "Historical": 0.2066, "BadTiming": 0.0885 }, "Balanced": { "Historical": 0.1805, "BadTiming": 0.0973 }, "Moderate": { "Historical": 0.1543, "BadTiming": 0.1061 } }, "26": { "Aggressive": { "Historical": 0.0854, "BadTiming": 0.2853 }, "Balanced": { "Historical": 0.0781, "BadTiming": 0.2764 }, "Moderate": { "Historical": 0.0707, "BadTiming": 0.2674 } }, "27": { "Aggressive": { "Historical": 0.0404, "BadTiming": 0.1717 }, "Balanced": { "Historical": 0.0379, "BadTiming": 0.1688 }, "Moderate": { "Historical": 0.0354, "BadTiming": 0.1658 } }, "28": { "Aggressive": { "Historical": 0.1189, "BadTiming": 0.0376 }, "Balanced": { "Historical": 0.1064, "BadTiming": 0.0345 }, "Moderate": { "Historical": 0.0939, "BadTiming": 0.0313 } }, "29": { "Aggressive": { "Historical": 0.0676, "BadTiming": 0.1293 }, "Balanced": { "Historical": 0.0722, "BadTiming": 0.1183 }, "Moderate": { "Historical": 0.0768, "BadTiming": 0.1074 } }, "30": { "Aggressive": { "Historical": -0.2192, "BadTiming": 0.2631 }, "Balanced": { "Historical": -0.1688, "BadTiming": 0.2463 }, "Moderate": { "Historical": -0.1185, "BadTiming": 0.2294 } }, "31": { "Aggressive": { "Historical": 0.1803, "BadTiming": 0.0051 }, "Balanced": { "Historical": 0.1521, "BadTiming": 0.0179 }, "Moderate": { "Historical": 0.124, "BadTiming": 0.0307 } }, "32": { "Aggressive": { "Historical": 0.1264, "BadTiming": 0.2595 }, "Balanced": { "Historical": 0.1189, "BadTiming": 0.2452 }, "Moderate": { "Historical": 0.1113, "BadTiming": 0.2309 } }, "33": { "Aggressive": { "Historical": 0.0431, "BadTiming": 0.0753 }, "Balanced": { "Historical": 0.051, "BadTiming": 0.0757 }, "Moderate": { "Historical": 0.0588, "BadTiming": 0.076 } }, "34": { "Aggressive": { "Historical": 0.1188, "BadTiming": 0.1035 }, "Balanced": { "Historical": 0.1056, "BadTiming": 0.1051 }, "Moderate": { "Historical": 0.0925, "BadTiming": 0.1066 } }, "35": { "Aggressive": { "Historical": 0.216, "BadTiming": -0.0047 }, "Balanced": { "Historical": 0.1807, "BadTiming": -0.0102 }, "Moderate": { "Historical": 0.1454, "BadTiming": -0.0158 } }, "36": { "Aggressive": { "Historical": 0.1075, "BadTiming": 0.3234 }, "Balanced": { "Historical": 0.0983, "BadTiming": 0.3064 }, "Moderate": { "Historical": 0.0891, "BadTiming": 0.2894 } }, "37": { "Aggressive": { "Historical": 0.0133, "BadTiming": 0.1659 }, "Balanced": { "Historical": 0.0135, "BadTiming": 0.1449 }, "Moderate": { "Historical": 0.0138, "BadTiming": 0.124 } }, "38": { "Aggressive": { "Historical": 0.0863, "BadTiming": 0.2592 }, "Balanced": { "Historical": 0.0756, "BadTiming": 0.235 }, "Moderate": { "Historical": 0.065, "BadTiming": 0.2108 } }, "39": { "Aggressive": { "Historical": 0.1564, "BadTiming": 0.2321 }, "Balanced": { "Historical": 0.1363, "BadTiming": 0.2141 }, "Moderate": { "Historical": 0.1162, "BadTiming": 0.1961 } }, "40": { "Aggressive": { "Historical": -0.0287, "BadTiming": 0.1369 }, "Balanced": { "Historical": -0.0232, "BadTiming": 0.1123 }, "Moderate": { "Historical": -0.0176, "BadTiming": 0.0877 } } };

// Uniform Lifetime Table (Table III) (https://www.fidelity.com/bin-public/060_www_fidelity_com/documents/UniformLifetimeTable.pdf)
//              age: 72,   73,   74,   75,   76,   77,   78,   79,   80,   81,   82,   83,   84,   85,   86,   87,   88,   89,   90,   91,   92,   93,  94,  95,  96,  97,  98,  99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120+
const TABLE3_1D = [27.4, 26.5, 25.5, 24.6, 23.7, 22.9, 22.0, 21.1, 20.2, 19.4, 18.5, 17.7, 16.8, 16.0, 15.2, 14.4, 13.7, 12.9, 12.2, 11.5, 10.8, 10.1, 9.5, 8.9, 8.4, 7.8, 7.3, 6.8, 6.4, 6.0, 5.6, 5.2, 4.9, 4.6, 4.3, 4.1, 3.9, 3.7, 3.5, 3.4, 3.3, 3.1, 3.0, 2.9, 2.8, 2.7, 2.5, 2.3, 2.0];

// Joint Life and Last Survivor Expectancy Table (Table II) (https://www.federalregister.gov/documents/2020/11/12/2020-24723/updated-life-expectancy-and-distribution-period-tables-used-for-purposes-of-determining-minimum)
const TABLE2_2D = [
    /*   0 */[91.9],
    /*   1 */[91.4, 90.9],
    /*   2 */[91.0, 90.4, 89.9],
    /*   3 */[90.5, 90.0, 89.4, 88.9],
    /*   4 */[90.1, 89.5, 89.0, 88.4, 87.9],
    /*   5 */[89.7, 89.1, 88.5, 88.0, 87.4, 86.9],
    /*   6 */[89.4, 88.8, 88.1, 87.6, 87.0, 86.5, 85.9],
    /*   7 */[89.0, 88.4, 87.8, 87.1, 86.6, 86.0, 85.5, 84.9],
    /*   8 */[88.7, 88.1, 87.4, 86.8, 86.2, 85.6, 85.0, 84.5, 83.9],
    /*   9 */[88.4, 87.8, 87.1, 86.4, 85.8, 85.2, 84.6, 84.0, 83.5, 82.9],
    /*  10 */[88.2, 87.5, 86.8, 86.1, 85.4, 84.8, 84.2, 83.6, 83.0, 82.5, 81.9],
    /*  11 */[87.9, 87.2, 86.5, 85.8, 85.1, 84.4, 83.8, 83.2, 82.6, 82.0, 81.5, 80.9],
    /*  12 */[87.7, 87.0, 86.2, 85.5, 84.8, 84.1, 83.4, 82.8, 82.2, 81.6, 81.0, 80.5, 79.9],
    /*  13 */[87.5, 86.7, 86.0, 85.2, 84.5, 83.8, 83.1, 82.4, 81.8, 81.2, 80.6, 80.0, 79.5, 79.0],
    /*  14 */[87.3, 86.5, 85.7, 85.0, 84.2, 83.5, 82.8, 82.1, 81.4, 80.8, 80.2, 79.6, 79.0, 78.5, 78.0],
    /*  15 */[87.1, 86.3, 85.5, 84.7, 84.0, 83.2, 82.5, 81.8, 81.1, 80.4, 79.8, 79.2, 78.6, 78.0, 77.5, 77.0],
    /*  16 */[86.9, 86.1, 85.3, 84.5, 83.7, 83.0, 82.2, 81.5, 80.8, 80.1, 79.4, 78.8, 78.2, 77.6, 77.0, 76.5, 76.0],
    /*  17 */[86.8, 86.0, 85.1, 84.3, 83.5, 82.7, 82.0, 81.2, 80.5, 79.8, 79.1, 78.4, 77.8, 77.2, 76.6, 76.0, 75.5, 75.0],
    /*  18 */[86.6, 85.8, 85.0, 84.1, 83.3, 82.5, 81.7, 81.0, 80.2, 79.5, 78.8, 78.1, 77.4, 76.8, 76.2, 75.6, 75.0, 74.5, 74.0],
    /*  19 */[86.5, 85.7, 84.8, 84.0, 83.1, 82.3, 81.5, 80.7, 80.0, 79.2, 78.5, 77.8, 77.1, 76.4, 75.8, 75.2, 74.6, 74.0, 73.5, 73.0],
    /*  20 */[86.4, 85.5, 84.7, 83.8, 83.0, 82.2, 81.3, 80.5, 79.8, 79.0, 78.2, 77.5, 76.8, 76.1, 75.4, 74.8, 74.2, 73.6, 73.0, 72.5, 72.0],
    /*  21 */[86.2, 85.4, 84.5, 83.7, 82.8, 82.0, 81.2, 80.3, 79.5, 78.8, 78.0, 77.2, 76.5, 75.8, 75.1, 74.4, 73.8, 73.2, 72.6, 72.0, 71.5, 71.0],
    /*  22 */[86.1, 85.3, 84.4, 83.5, 82.7, 81.8, 81.0, 80.2, 79.3, 78.5, 77.8, 77.0, 76.2, 75.5, 74.8, 74.1, 73.4, 72.8, 72.2, 71.6, 71.0, 70.5, 70.0],
    /*  23 */[86.0, 85.2, 84.3, 83.4, 82.5, 81.7, 80.8, 80.0, 79.2, 78.3, 77.5, 76.8, 76.0, 75.2, 74.5, 73.8, 73.1, 72.5, 71.8, 71.2, 70.6, 70.0, 69.5, 69.0],
    /*  24 */[85.9, 85.1, 84.2, 83.3, 82.4, 81.6, 80.7, 79.8, 79.0, 78.2, 77.3, 76.5, 75.8, 75.0, 74.2, 73.5, 72.8, 72.1, 71.5, 70.8, 70.2, 69.6, 69.0, 68.5, 68.0],
    /*  25 */[85.9, 85.0, 84.1, 83.2, 82.3, 81.4, 80.6, 79.7, 78.8, 78.0, 77.2, 76.4, 75.6, 74.8, 74.0, 73.3, 72.5, 71.8, 71.1, 70.5, 69.8, 69.2, 68.6, 68.0, 67.5, 67.0],
    /*  26 */[85.8, 84.9, 84.0, 83.1, 82.2, 81.3, 80.4, 79.6, 78.7, 77.8, 77.0, 76.2, 75.4, 74.6, 73.8, 73.0, 72.3, 71.5, 70.8, 70.1, 69.5, 68.8, 68.2, 67.6, 67.1, 66.5, 66.0],
    /*  27 */[85.7, 84.8, 83.9, 83.0, 82.1, 81.2, 80.3, 79.4, 78.6, 77.7, 76.8, 76.0, 75.2, 74.4, 73.6, 72.8, 72.0, 71.3, 70.5, 69.8, 69.1, 68.5, 67.8, 67.2, 66.6, 66.1, 65.5, 65.0],
    /*  28 */[85.6, 84.7, 83.8, 82.9, 82.0, 81.1, 80.2, 79.3, 78.4, 77.6, 76.7, 75.8, 75.0, 74.2, 73.4, 72.6, 71.8, 71.0, 70.3, 69.5, 68.8, 68.1, 67.5, 66.8, 66.2, 65.6, 65.1, 64.5, 64.0],
    /*  29 */[85.6, 84.7, 83.8, 82.8, 81.9, 81.0, 80.1, 79.2, 78.3, 77.4, 76.6, 75.7, 74.9, 74.0, 73.2, 72.4, 71.6, 70.8, 70.0, 69.3, 68.5, 67.8, 67.1, 66.5, 65.8, 65.2, 64.6, 64.1, 63.5, 63.0],
    /*  30 */[85.5, 84.6, 83.7, 82.8, 81.8, 80.9, 80.0, 79.1, 78.2, 77.3, 76.4, 75.6, 74.7, 73.9, 73.0, 72.2, 71.4, 70.6, 69.8, 69.0, 68.3, 67.5, 66.8, 66.2, 65.5, 64.9, 64.2, 63.7, 63.1, 62.6, 62.0],
    /*  31 */[85.4, 84.6, 83.6, 82.7, 81.8, 80.9, 79.9, 79.0, 78.1, 77.2, 76.3, 75.5, 74.6, 73.7, 72.9, 72.0, 71.2, 70.4, 69.6, 68.8, 68.0, 67.3, 66.6, 65.8, 65.2, 64.5, 63.9, 63.2, 62.7, 62.1, 61.6, 61.1],
    /*  32 */[85.4, 84.5, 83.6, 82.6, 81.7, 80.8, 79.9, 78.9, 78.0, 77.1, 76.2, 75.3, 74.5, 73.6, 72.7, 71.9, 71.0, 70.2, 69.4, 68.6, 67.8, 67.0, 66.3, 65.6, 64.9, 64.2, 63.5, 62.9, 62.3, 61.7, 61.1, 60.6, 60.1],
    /*  33 */[85.3, 84.5, 83.5, 82.6, 81.6, 80.7, 79.8, 78.9, 77.9, 77.0, 76.1, 75.2, 74.3, 73.5, 72.6, 71.7, 70.9, 70.0, 69.2, 68.4, 67.6, 66.8, 66.0, 65.3, 64.6, 63.9, 63.2, 62.5, 61.9, 61.3, 60.7, 60.1, 59.6, 59.1],
    /*  34 */[85.3, 84.4, 83.5, 82.5, 81.6, 80.7, 79.7, 78.8, 77.9, 77.0, 76.0, 75.1, 74.2, 73.3, 72.5, 71.6, 70.7, 69.9, 69.0, 68.2, 67.4, 66.6, 65.8, 65.1, 64.3, 63.6, 62.9, 62.2, 61.5, 60.9, 60.3, 59.7, 59.1, 58.6, 58.1],
    /*  35 */[85.3, 84.4, 83.4, 82.5, 81.5, 80.6, 79.7, 78.7, 77.8, 76.9, 76.0, 75.0, 74.1, 73.2, 72.4, 71.5, 70.6, 69.7, 68.9, 68.0, 67.2, 66.4, 65.6, 64.8, 64.1, 63.3, 62.6, 61.9, 61.2, 60.5, 59.9, 59.3, 58.7, 58.1, 57.6, 57.1],
    /*  36 */[85.2, 84.3, 83.4, 82.4, 81.5, 80.5, 79.6, 78.7, 77.7, 76.8, 75.9, 75.0, 74.0, 73.1, 72.2, 71.4, 70.5, 69.6, 68.7, 67.9, 67.1, 66.2, 65.4, 64.6, 63.8, 63.1, 62.3, 61.6, 60.9, 60.2, 59.5, 58.9, 58.3, 57.7, 57.2, 56.6, 56.1],
    /*  37 */[85.2, 84.3, 83.3, 82.4, 81.4, 80.5, 79.5, 78.6, 77.7, 76.7, 75.8, 74.9, 74.0, 73.1, 72.1, 71.3, 70.4, 69.5, 68.6, 67.7, 66.9, 66.1, 65.2, 64.4, 63.6, 62.8, 62.1, 61.3, 60.6, 59.9, 59.2, 58.6, 57.9, 57.3, 56.7, 56.2, 55.6, 55.1],
    /*  38 */[85.2, 84.3, 83.3, 82.3, 81.4, 80.4, 79.5, 78.6, 77.6, 76.7, 75.7, 74.8, 73.9, 73.0, 72.1, 71.2, 70.3, 69.4, 68.5, 67.6, 66.8, 65.9, 65.1, 64.2, 63.4, 62.6, 61.9, 61.1, 60.3, 59.6, 58.9, 58.2, 57.6, 56.9, 56.3, 55.7, 55.2, 54.6, 54.1],
    /*  39 */[85.1, 84.2, 83.3, 82.3, 81.4, 80.4, 79.5, 78.5, 77.6, 76.6, 75.7, 74.7, 73.8, 72.9, 72.0, 71.1, 70.2, 69.3, 68.4, 67.5, 66.6, 65.8, 64.9, 64.1, 63.3, 62.4, 61.6, 60.9, 60.1, 59.4, 58.6, 57.9, 57.2, 56.6, 55.9, 55.3, 54.7, 54.2, 53.6, 53.1],
    /*  40 */[85.1, 84.2, 83.2, 82.3, 81.3, 80.4, 79.4, 78.5, 77.5, 76.6, 75.6, 74.7, 73.7, 72.8, 71.9, 71.0, 70.1, 69.2, 68.3, 67.4, 66.5, 65.6, 64.8, 63.9, 63.1, 62.3, 61.5, 60.7, 59.9, 59.1, 58.4, 57.6, 56.9, 56.3, 55.6, 55.0, 54.3, 53.8, 53.2, 52.7, 52.2],
    /*  41 */[85.1, 84.2, 83.2, 82.2, 81.3, 80.3, 79.4, 78.4, 77.5, 76.5, 75.6, 74.6, 73.7, 72.8, 71.8, 70.9, 70.0, 69.1, 68.2, 67.3, 66.4, 65.5, 64.6, 63.8, 62.9, 62.1, 61.3, 60.5, 59.7, 58.9, 58.1, 57.4, 56.7, 56.0, 55.3, 54.6, 54.0, 53.4, 52.8, 52.2, 51.7, 51.2],
    /*  42 */[85.0, 84.1, 83.2, 82.2, 81.3, 80.3, 79.3, 78.4, 77.4, 76.5, 75.5, 74.6, 73.6, 72.7, 71.8, 70.8, 69.9, 69.0, 68.1, 67.2, 66.3, 65.4, 64.5, 63.6, 62.8, 61.9, 61.1, 60.3, 59.5, 58.7, 57.9, 57.1, 56.4, 55.7, 55.0, 54.3, 53.6, 53.0, 52.4, 51.8, 51.2, 50.7, 50.2],
    /*  43 */[85.0, 84.1, 83.1, 82.2, 81.2, 80.3, 79.3, 78.3, 77.4, 76.4, 75.5, 74.5, 73.6, 72.6, 71.7, 70.8, 69.8, 68.9, 68.0, 67.1, 66.2, 65.3, 64.4, 63.5, 62.7, 61.8, 61.0, 60.1, 59.3, 58.5, 57.7, 56.9, 56.2, 55.4, 54.7, 54.0, 53.3, 52.6, 52.0, 51.4, 50.8, 50.2, 49.7, 49.2],
    /*  44 */[85.0, 84.1, 83.1, 82.2, 81.2, 80.2, 79.3, 78.3, 77.3, 76.4, 75.4, 74.5, 73.5, 72.6, 71.6, 70.7, 69.8, 68.8, 67.9, 67.0, 66.1, 65.2, 64.3, 63.4, 62.5, 61.7, 60.8, 60.0, 59.1, 58.3, 57.5, 56.7, 55.9, 55.2, 54.4, 53.7, 53.0, 52.3, 51.6, 51.0, 50.4, 49.8, 49.2, 48.7, 48.2],
    /*  45 */[85.0, 84.1, 83.1, 82.1, 81.2, 80.2, 79.2, 78.3, 77.3, 76.4, 75.4, 74.4, 73.5, 72.5, 71.6, 70.6, 69.7, 68.8, 67.9, 66.9, 66.0, 65.1, 64.2, 63.3, 62.4, 61.5, 60.7, 59.8, 59.0, 58.1, 57.3, 56.5, 55.7, 54.9, 54.2, 53.4, 52.7, 52.0, 51.3, 50.7, 50.0, 49.4, 48.8, 48.3, 47.7, 47.2],
    /*  46 */[84.9, 84.0, 83.1, 82.1, 81.1, 80.2, 79.2, 78.2, 77.3, 76.3, 75.4, 74.4, 73.4, 72.5, 71.5, 70.6, 69.7, 68.7, 67.8, 66.9, 65.9, 65.0, 64.1, 63.2, 62.3, 61.4, 60.6, 59.7, 58.8, 58.0, 57.2, 56.3, 55.5, 54.7, 54.0, 53.2, 52.4, 51.7, 51.0, 50.3, 49.7, 49.0, 48.4, 47.8, 47.3, 46.7, 46.2],
    /*  47 */[84.9, 84.0, 83.1, 82.1, 81.1, 80.2, 79.2, 78.2, 77.3, 76.3, 75.3, 74.4, 73.4, 72.4, 71.5, 70.5, 69.6, 68.7, 67.7, 66.8, 65.9, 65.0, 64.0, 63.1, 62.2, 61.3, 60.5, 59.6, 58.7, 57.9, 57.0, 56.2, 55.4, 54.5, 53.7, 53.0, 52.2, 51.5, 50.7, 50.0, 49.3, 48.7, 48.0, 47.4, 46.8, 46.3, 45.7, 45.2],
    /*  48 */[84.9, 84.0, 83.0, 82.1, 81.1, 80.1, 79.2, 78.2, 77.2, 76.3, 75.3, 74.3, 73.4, 72.4, 71.5, 70.5, 69.6, 68.6, 67.7, 66.7, 65.8, 64.9, 64.0, 63.0, 62.1, 61.2, 60.3, 59.5, 58.6, 57.7, 56.9, 56.0, 55.2, 54.4, 53.6, 52.8, 52.0, 51.2, 50.5, 49.7, 49.0, 48.4, 47.7, 47.1, 46.4, 45.9, 45.3, 44.8, 44.3],
    /*  49 */[84.9, 84.0, 83.0, 82.1, 81.1, 80.1, 79.1, 78.2, 77.2, 76.2, 75.3, 74.3, 73.3, 72.4, 71.4, 70.5, 69.5, 68.6, 67.6, 66.7, 65.7, 64.8, 63.9, 63.0, 62.1, 61.2, 60.3, 59.4, 58.5, 57.6, 56.7, 55.9, 55.0, 54.2, 53.4, 52.6, 51.8, 51.0, 50.2, 49.5, 48.8, 48.1, 47.4, 46.7, 46.1, 45.5, 44.9, 44.3, 43.8, 43.3],
    /*  50 */[84.9, 84.0, 83.0, 82.0, 81.1, 80.1, 79.1, 78.1, 77.2, 76.2, 75.2, 74.3, 73.3, 72.3, 71.4, 70.4, 69.5, 68.5, 67.6, 66.6, 65.7, 64.8, 63.8, 62.9, 62.0, 61.1, 60.2, 59.3, 58.4, 57.5, 56.6, 55.8, 54.9, 54.1, 53.2, 52.4, 51.6, 50.8, 50.0, 49.2, 48.5, 47.8, 47.1, 46.4, 45.7, 45.1, 44.5, 43.9, 43.3, 42.8, 42.3],
    /*  51 */[84.8, 84.0, 83.0, 82.0, 81.0, 80.1, 79.1, 78.1, 77.2, 76.2, 75.2, 74.2, 73.3, 72.3, 71.3, 70.4, 69.4, 68.5, 67.5, 66.6, 65.6, 64.7, 63.8, 62.8, 61.9, 61.0, 60.1, 59.2, 58.3, 57.4, 56.5, 55.6, 54.8, 53.9, 53.1, 52.2, 51.4, 50.6, 49.8, 49.0, 48.3, 47.5, 46.8, 46.1, 45.4, 44.7, 44.1, 43.5, 42.9, 42.3, 41.8, 41.3],
    /*  52 */[84.8, 83.9, 83.0, 82.0, 81.0, 80.1, 79.1, 78.1, 77.1, 76.2, 75.2, 74.2, 73.2, 72.3, 71.3, 70.4, 69.4, 68.4, 67.5, 66.5, 65.6, 64.7, 63.7, 62.8, 61.9, 60.9, 60.0, 59.1, 58.2, 57.3, 56.4, 55.5, 54.7, 53.8, 52.9, 52.1, 51.3, 50.4, 49.6, 48.8, 48.0, 47.3, 46.5, 45.8, 45.1, 44.4, 43.8, 43.1, 42.5, 41.9, 41.4, 40.8, 40.3],
    /*  53 */[84.8, 83.9, 83.0, 82.0, 81.0, 80.0, 79.1, 78.1, 77.1, 76.1, 75.2, 74.2, 73.2, 72.3, 71.3, 70.3, 69.4, 68.4, 67.4, 66.5, 65.5, 64.6, 63.7, 62.7, 61.8, 60.9, 59.9, 59.0, 58.1, 57.2, 56.3, 55.4, 54.6, 53.7, 52.8, 52.0, 51.1, 50.3, 49.5, 48.6, 47.8, 47.1, 46.3, 45.6, 44.8, 44.1, 43.4, 42.8, 42.1, 41.5, 40.9, 40.4, 39.9, 39.4],
    /*  54 */[84.8, 83.9, 82.9, 82.0, 81.0, 80.0, 79.0, 78.1, 77.1, 76.1, 75.1, 74.2, 73.2, 72.2, 71.3, 70.3, 69.3, 68.4, 67.4, 66.5, 65.5, 64.6, 63.6, 62.7, 61.7, 60.8, 59.9, 59.0, 58.0, 57.1, 56.2, 55.3, 54.5, 53.6, 52.7, 51.8, 51.0, 50.1, 49.3, 48.5, 47.7, 46.9, 46.1, 45.3, 44.6, 43.8, 43.1, 42.5, 41.8, 41.2, 40.6, 40.0, 39.4, 38.9, 38.4],
    /*  55 */[84.8, 83.9, 82.9, 82.0, 81.0, 80.0, 79.0, 78.1, 77.1, 76.1, 75.1, 74.2, 73.2, 72.2, 71.2, 70.3, 69.3, 68.3, 67.4, 66.4, 65.5, 64.5, 63.6, 62.6, 61.7, 60.8, 59.8, 58.9, 58.0, 57.1, 56.2, 55.3, 54.4, 53.5, 52.6, 51.7, 50.9, 50.0, 49.1, 48.3, 47.5, 46.7, 45.9, 45.1, 44.3, 43.6, 42.9, 42.2, 41.5, 40.8, 40.2, 39.6, 39.0, 38.4, 37.9, 37.4],
    /*  56 */[84.8, 83.9, 82.9, 81.9, 81.0, 80.0, 79.0, 78.0, 77.1, 76.1, 75.1, 74.1, 73.2, 72.2, 71.2, 70.2, 69.3, 68.3, 67.4, 66.4, 65.4, 64.5, 63.5, 62.6, 61.6, 60.7, 59.8, 58.8, 57.9, 57.0, 56.1, 55.2, 54.3, 53.4, 52.5, 51.6, 50.7, 49.9, 49.0, 48.2, 47.3, 46.5, 45.7, 44.9, 44.1, 43.4, 42.6, 41.9, 41.2, 40.5, 39.8, 39.2, 38.6, 38.0, 37.5, 36.9, 36.5],
    /*  57 */[84.8, 83.9, 82.9, 81.9, 81.0, 80.0, 79.0, 78.0, 77.0, 76.1, 75.1, 74.1, 73.1, 72.2, 71.2, 70.2, 69.3, 68.3, 67.3, 66.4, 65.4, 64.5, 63.5, 62.5, 61.6, 60.7, 59.7, 58.8, 57.9, 56.9, 56.0, 55.1, 54.2, 53.3, 52.4, 51.5, 50.6, 49.8, 48.9, 48.0, 47.2, 46.3, 45.5, 44.7, 43.9, 43.1, 42.4, 41.6, 40.9, 40.2, 39.5, 38.9, 38.2, 37.6, 37.1, 36.5, 36.0, 35.5],
    /*  58 */[84.8, 83.9, 82.9, 81.9, 80.9, 80.0, 79.0, 78.0, 77.0, 76.1, 75.1, 74.1, 73.1, 72.1, 71.2, 70.2, 69.2, 68.3, 67.3, 66.3, 65.4, 64.4, 63.5, 62.5, 61.6, 60.6, 59.7, 58.7, 57.8, 56.9, 56.0, 55.0, 54.1, 53.2, 52.3, 51.4, 50.5, 49.7, 48.8, 47.9, 47.1, 46.2, 45.4, 44.5, 43.7, 42.9, 42.2, 41.4, 40.7, 39.9, 39.2, 38.6, 37.9, 37.3, 36.7, 36.1, 35.5, 35.0, 34.5],
    /*  59 */[84.7, 83.9, 82.9, 81.9, 80.9, 80.0, 79.0, 78.0, 77.0, 76.0, 75.1, 74.1, 73.1, 72.1, 71.2, 70.2, 69.2, 68.2, 67.3, 66.3, 65.4, 64.4, 63.4, 62.5, 61.5, 60.6, 59.6, 58.7, 57.8, 56.8, 55.9, 55.0, 54.1, 53.2, 52.2, 51.3, 50.5, 49.6, 48.7, 47.8, 46.9, 46.1, 45.2, 44.4, 43.6, 42.8, 42.0, 41.2, 40.4, 39.7, 39.0, 38.3, 37.6, 36.9, 36.3, 35.7, 35.1, 34.6, 34.1, 33.6],
    /*  60 */[84.7, 83.8, 82.9, 81.9, 80.9, 79.9, 79.0, 78.0, 77.0, 76.0, 75.0, 74.1, 73.1, 72.1, 71.1, 70.2, 69.2, 68.2, 67.3, 66.3, 65.3, 64.4, 63.4, 62.4, 61.5, 60.5, 59.6, 58.7, 57.7, 56.8, 55.9, 54.9, 54.0, 53.1, 52.2, 51.3, 50.4, 49.5, 48.6, 47.7, 46.8, 46.0, 45.1, 44.3, 43.4, 42.6, 41.8, 41.0, 40.2, 39.5, 38.7, 38.0, 37.3, 36.6, 36.0, 35.3, 34.8, 34.2, 33.6, 33.1, 32.6],
    /*  61 */[84.7, 83.8, 82.9, 81.9, 80.9, 79.9, 79.0, 78.0, 77.0, 76.0, 75.0, 74.1, 73.1, 72.1, 71.1, 70.1, 69.2, 68.2, 67.2, 66.3, 65.3, 64.3, 63.4, 62.4, 61.5, 60.5, 59.6, 58.6, 57.7, 56.7, 55.8, 54.9, 54.0, 53.0, 52.1, 51.2, 50.3, 49.4, 48.5, 47.6, 46.7, 45.8, 45.0, 44.1, 43.3, 42.4, 41.6, 40.8, 40.0, 39.2, 38.5, 37.7, 37.0, 36.3, 35.7, 35.0, 34.4, 33.8, 33.2, 32.7, 32.2, 31.7],
    /*  62 */[84.7, 83.8, 82.9, 81.9, 80.9, 79.9, 78.9, 78.0, 77.0, 76.0, 75.0, 74.0, 73.1, 72.1, 71.1, 70.1, 69.2, 68.2, 67.2, 66.2, 65.3, 64.3, 63.4, 62.4, 61.4, 60.5, 59.5, 58.6, 57.6, 56.7, 55.8, 54.8, 53.9, 53.0, 52.1, 51.1, 50.2, 49.3, 48.4, 47.5, 46.6, 45.7, 44.9, 44.0, 43.1, 42.3, 41.5, 40.6, 39.8, 39.0, 38.3, 37.5, 36.8, 36.1, 35.4, 34.7, 34.1, 33.4, 32.8, 32.3, 31.7, 31.2, 30.8],
    /*  63 */[84.7, 83.8, 82.9, 81.9, 80.9, 79.9, 78.9, 78.0, 77.0, 76.0, 75.0, 74.0, 73.0, 72.1, 71.1, 70.1, 69.1, 68.2, 67.2, 66.2, 65.3, 64.3, 63.3, 62.4, 61.4, 60.5, 59.5, 58.6, 57.6, 56.7, 55.7, 54.8, 53.9, 52.9, 52.0, 51.1, 50.2, 49.3, 48.3, 47.4, 46.5, 45.7, 44.8, 43.9, 43.0, 42.2, 41.3, 40.5, 39.7, 38.9, 38.1, 37.3, 36.6, 35.8, 35.1, 34.4, 33.8, 33.1, 32.5, 31.9, 31.3, 30.8, 30.3, 29.8],
    /*  64 */[84.7, 83.8, 82.8, 81.9, 80.9, 79.9, 78.9, 77.9, 77.0, 76.0, 75.0, 74.0, 73.0, 72.1, 71.1, 70.1, 69.1, 68.2, 67.2, 66.2, 65.2, 64.3, 63.3, 62.3, 61.4, 60.4, 59.5, 58.5, 57.6, 56.6, 55.7, 54.8, 53.8, 52.9, 52.0, 51.0, 50.1, 49.2, 48.3, 47.4, 46.5, 45.6, 44.7, 43.8, 42.9, 42.1, 41.2, 40.4, 39.5, 38.7, 37.9, 37.1, 36.3, 35.6, 34.9, 34.2, 33.5, 32.8, 32.2, 31.5, 31.0, 30.4, 29.9, 29.4, 28.9],
    /*  65 */[84.7, 83.8, 82.8, 81.9, 80.9, 79.9, 78.9, 77.9, 77.0, 76.0, 75.0, 74.0, 73.0, 72.0, 71.1, 70.1, 69.1, 68.1, 67.2, 66.2, 65.2, 64.3, 63.3, 62.3, 61.4, 60.4, 59.5, 58.5, 57.5, 56.6, 55.7, 54.7, 53.8, 52.8, 51.9, 51.0, 50.1, 49.1, 48.2, 47.3, 46.4, 45.5, 44.6, 43.7, 42.8, 41.9, 41.1, 40.2, 39.4, 38.6, 37.7, 36.9, 36.2, 35.4, 34.6, 33.9, 33.2, 32.5, 31.9, 31.2, 30.6, 30.0, 29.5, 28.9, 28.4, 28.0],
    /*  66 */[84.7, 83.8, 82.8, 81.9, 80.9, 79.9, 78.9, 77.9, 76.9, 76.0, 75.0, 74.0, 73.0, 72.0, 71.1, 70.1, 69.1, 68.1, 67.2, 66.2, 65.2, 64.2, 63.3, 62.3, 61.3, 60.4, 59.4, 58.5, 57.5, 56.6, 55.6, 54.7, 53.7, 52.8, 51.9, 50.9, 50.0, 49.1, 48.2, 47.2, 46.3, 45.4, 44.5, 43.6, 42.7, 41.8, 41.0, 40.1, 39.3, 38.4, 37.6, 36.8, 36.0, 35.2, 34.4, 33.7, 33.0, 32.3, 31.6, 30.9, 30.3, 29.7, 29.1, 28.5, 28.0, 27.5, 27.0],
    /*  67 */[84.7, 83.8, 82.8, 81.9, 80.9, 79.9, 78.9, 77.9, 76.9, 76.0, 75.0, 74.0, 73.0, 72.0, 71.0, 70.1, 69.1, 68.1, 67.1, 66.2, 65.2, 64.2, 63.3, 62.3, 61.3, 60.4, 59.4, 58.5, 57.5, 56.5, 55.6, 54.7, 53.7, 52.8, 51.8, 50.9, 50.0, 49.0, 48.1, 47.2, 46.3, 45.4, 44.4, 43.5, 42.6, 41.8, 40.9, 40.0, 39.1, 38.3, 37.5, 36.6, 35.8, 35.0, 34.2, 33.5, 32.7, 32.0, 31.3, 30.6, 30.0, 29.4, 28.7, 28.2, 27.6, 27.1, 26.6, 26.1],
    /*  68 */[84.7, 83.8, 82.8, 81.8, 80.9, 79.9, 78.9, 77.9, 76.9, 75.9, 75.0, 74.0, 73.0, 72.0, 71.0, 70.1, 69.1, 68.1, 67.1, 66.2, 65.2, 64.2, 63.2, 62.3, 61.3, 60.3, 59.4, 58.4, 57.5, 56.5, 55.6, 54.6, 53.7, 52.7, 51.8, 50.9, 49.9, 49.0, 48.1, 47.1, 46.2, 45.3, 44.4, 43.5, 42.6, 41.7, 40.8, 39.9, 39.0, 38.2, 37.3, 36.5, 35.7, 34.9, 34.1, 33.3, 32.5, 31.8, 31.1, 30.4, 29.7, 29.1, 28.4, 27.8, 27.2, 26.7, 26.2, 25.7, 25.2],
    /*  69 */[84.7, 83.8, 82.8, 81.8, 80.9, 79.9, 78.9, 77.9, 76.9, 75.9, 75.0, 74.0, 73.0, 72.0, 71.0, 70.0, 69.1, 68.1, 67.1, 66.1, 65.2, 64.2, 63.2, 62.3, 61.3, 60.3, 59.4, 58.4, 57.5, 56.5, 55.6, 54.6, 53.7, 52.7, 51.8, 50.8, 49.9, 49.0, 48.0, 47.1, 46.2, 45.2, 44.3, 43.4, 42.5, 41.6, 40.7, 39.8, 38.9, 38.1, 37.2, 36.4, 35.5, 34.7, 33.9, 33.1, 32.3, 31.6, 30.9, 30.1, 29.4, 28.8, 28.1, 27.5, 26.9, 26.3, 25.8, 25.3, 24.8, 24.3],
    /*  70 */[84.7, 83.8, 82.8, 81.8, 80.9, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 74.0, 73.0, 72.0, 71.0, 70.0, 69.1, 68.1, 67.1, 66.1, 65.2, 64.2, 63.2, 62.2, 61.3, 60.3, 59.4, 58.4, 57.4, 56.5, 55.5, 54.6, 53.6, 52.7, 51.7, 50.8, 49.9, 48.9, 48.0, 47.0, 46.1, 45.2, 44.3, 43.3, 42.4, 41.5, 40.6, 39.7, 38.8, 38.0, 37.1, 36.2, 35.4, 34.6, 33.8, 33.0, 32.2, 31.4, 30.7, 29.9, 29.2, 28.5, 27.9, 27.2, 26.6, 26.0, 25.4, 24.9, 24.3, 23.9, 23.4],
    /*  71 */[84.7, 83.8, 82.8, 81.8, 80.9, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 74.0, 73.0, 72.0, 71.0, 70.0, 69.0, 68.1, 67.1, 66.1, 65.1, 64.2, 63.2, 62.2, 61.3, 60.3, 59.3, 58.4, 57.4, 56.5, 55.5, 54.6, 53.6, 52.7, 51.7, 50.8, 49.8, 48.9, 47.9, 47.0, 46.1, 45.1, 44.2, 43.3, 42.4, 41.5, 40.6, 39.7, 38.8, 37.9, 37.0, 36.1, 35.3, 34.5, 33.6, 32.8, 32.0, 31.2, 30.5, 29.7, 29.0, 28.3, 27.6, 26.9, 26.3, 25.7, 25.1, 24.5, 24.0, 23.4, 22.9, 22.5],
    /*  72 */[84.7, 83.8, 82.8, 81.8, 80.9, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 73.0, 72.0, 71.0, 70.0, 69.0, 68.1, 67.1, 66.1, 65.1, 64.2, 63.2, 62.2, 61.3, 60.3, 59.3, 58.4, 57.4, 56.5, 55.5, 54.5, 53.6, 52.6, 51.7, 50.8, 49.8, 48.9, 47.9, 47.0, 46.0, 45.1, 44.2, 43.2, 42.3, 41.4, 40.5, 39.6, 38.7, 37.8, 36.9, 36.0, 35.2, 34.3, 33.5, 32.7, 31.9, 31.1, 30.3, 29.5, 28.8, 28.1, 27.4, 26.7, 26.0, 25.4, 24.8, 24.2, 23.6, 23.1, 22.5, 22.0, 21.6],
    /*  73 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 73.0, 72.0, 71.0, 70.0, 69.0, 68.1, 67.1, 66.1, 65.1, 64.2, 63.2, 62.2, 61.2, 60.3, 59.3, 58.4, 57.4, 56.4, 55.5, 54.5, 53.6, 52.6, 51.7, 50.7, 49.8, 48.8, 47.9, 46.9, 46.0, 45.1, 44.1, 43.2, 42.3, 41.4, 40.4, 39.5, 38.6, 37.7, 36.8, 36.0, 35.1, 34.2, 33.4, 32.6, 31.7, 30.9, 30.1, 29.4, 28.6, 27.9, 27.2, 26.5, 25.8, 25.1, 24.5, 23.9, 23.3, 22.7, 22.2, 21.6, 21.1, 20.7],
    /*  74 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 73.0, 72.0, 71.0, 70.0, 69.0, 68.0, 67.1, 66.1, 65.1, 64.1, 63.2, 62.2, 61.2, 60.3, 59.3, 58.3, 57.4, 56.4, 55.5, 54.5, 53.6, 52.6, 51.7, 50.7, 49.8, 48.8, 47.9, 46.9, 46.0, 45.0, 44.1, 43.2, 42.2, 41.3, 40.4, 39.5, 38.6, 37.7, 36.8, 35.9, 35.0, 34.1, 33.3, 32.4, 31.6, 30.8, 30.0, 29.2, 28.4, 27.7, 27.0, 26.2, 25.5, 24.9, 24.2, 23.6, 23.0, 22.4, 21.8, 21.3, 20.7, 20.3, 19.8],
    /*  75 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 72.0, 71.0, 70.0, 69.0, 68.0, 67.1, 66.1, 65.1, 64.1, 63.2, 62.2, 61.2, 60.3, 59.3, 58.3, 57.4, 56.4, 55.5, 54.5, 53.5, 52.6, 51.6, 50.7, 49.7, 48.8, 47.8, 46.9, 45.9, 45.0, 44.1, 43.1, 42.2, 41.3, 40.3, 39.4, 38.5, 37.6, 36.7, 35.8, 34.9, 34.1, 33.2, 32.4, 31.5, 30.7, 29.9, 29.1, 28.3, 27.5, 26.8, 26.1, 25.3, 24.6, 24.0, 23.3, 22.7, 22.1, 21.5, 20.9, 20.4, 19.9, 19.4, 18.9],
    /*  76 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.9, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 72.0, 71.0, 70.0, 69.0, 68.0, 67.1, 66.1, 65.1, 64.1, 63.2, 62.2, 61.2, 60.2, 59.3, 58.3, 57.4, 56.4, 55.4, 54.5, 53.5, 52.6, 51.6, 50.7, 49.7, 48.8, 47.8, 46.9, 45.9, 45.0, 44.0, 43.1, 42.2, 41.2, 40.3, 39.4, 38.5, 37.5, 36.6, 35.7, 34.9, 34.0, 33.1, 32.3, 31.4, 30.6, 29.8, 29.0, 28.2, 27.4, 26.6, 25.9, 25.2, 24.4, 23.7, 23.1, 22.4, 21.8, 21.2, 20.6, 20.0, 19.5, 19.0, 18.5, 18.1],
    /*  77 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 72.0, 71.0, 70.0, 69.0, 68.0, 67.0, 66.1, 65.1, 64.1, 63.1, 62.2, 61.2, 60.2, 59.3, 58.3, 57.3, 56.4, 55.4, 54.5, 53.5, 52.6, 51.6, 50.7, 49.7, 48.8, 47.8, 46.9, 45.9, 45.0, 44.0, 43.1, 42.1, 41.2, 40.3, 39.3, 38.4, 37.5, 36.6, 35.7, 34.8, 33.9, 33.0, 32.2, 31.3, 30.5, 29.7, 28.8, 28.0, 27.3, 26.5, 25.7, 25.0, 24.3, 23.5, 22.9, 22.2, 21.5, 20.9, 20.3, 19.7, 19.1, 18.6, 18.1, 17.7, 17.2],
    /*  78 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 71.0, 70.0, 69.0, 68.0, 67.0, 66.1, 65.1, 64.1, 63.1, 62.2, 61.2, 60.2, 59.3, 58.3, 57.3, 56.4, 55.4, 54.5, 53.5, 52.6, 51.6, 50.6, 49.7, 48.7, 47.8, 46.8, 45.9, 44.9, 44.0, 43.0, 42.1, 41.2, 40.2, 39.3, 38.4, 37.5, 36.5, 35.6, 34.7, 33.9, 33.0, 32.1, 31.2, 30.4, 29.6, 28.7, 27.9, 27.1, 26.4, 25.6, 24.8, 24.1, 23.4, 22.7, 22.0, 21.3, 20.6, 20.0, 19.4, 18.8, 18.3, 17.8, 17.3, 16.8, 16.4],
    /*  79 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 71.0, 70.0, 69.0, 68.0, 67.0, 66.1, 65.1, 64.1, 63.1, 62.2, 61.2, 60.2, 59.3, 58.3, 57.3, 56.4, 55.4, 54.5, 53.5, 52.5, 51.6, 50.6, 49.7, 48.7, 47.8, 46.8, 45.9, 44.9, 44.0, 43.0, 42.1, 41.1, 40.2, 39.3, 38.3, 37.4, 36.5, 35.6, 34.7, 33.8, 32.9, 32.0, 31.2, 30.3, 29.5, 28.7, 27.8, 27.0, 26.2, 25.5, 24.7, 23.9, 23.2, 22.5, 21.8, 21.1, 20.4, 19.8, 19.2, 18.6, 18.0, 17.4, 16.9, 16.4, 16.0, 15.6],
    /*  80 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 71.0, 70.0, 69.0, 68.0, 67.0, 66.1, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.4, 55.4, 54.4, 53.5, 52.5, 51.6, 50.6, 49.7, 48.7, 47.8, 46.8, 45.9, 44.9, 43.9, 43.0, 42.1, 41.1, 40.2, 39.2, 38.3, 37.4, 36.5, 35.5, 34.6, 33.7, 32.9, 32.0, 31.1, 30.3, 29.4, 28.6, 27.8, 26.9, 26.1, 25.3, 24.6, 23.8, 23.1, 22.3, 21.6, 20.9, 20.2, 19.6, 18.9, 18.3, 17.7, 17.1, 16.6, 16.1, 15.6, 15.2, 14.7],
    /*  81 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 71.0, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.4, 55.4, 54.4, 53.5, 52.5, 51.6, 50.6, 49.7, 48.7, 47.7, 46.8, 45.8, 44.9, 43.9, 43.0, 42.0, 41.1, 40.1, 39.2, 38.3, 37.3, 36.4, 35.5, 34.6, 33.7, 32.8, 31.9, 31.1, 30.2, 29.3, 28.5, 27.7, 26.9, 26.0, 25.2, 24.5, 23.7, 22.9, 22.2, 21.5, 20.7, 20.0, 19.4, 18.7, 18.1, 17.4, 16.9, 16.3, 15.8, 15.3, 14.8, 14.4, 14.0],
    /*  82 */[84.6, 83.8, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.3, 55.4, 54.4, 53.5, 52.5, 51.6, 50.6, 49.7, 48.7, 47.7, 46.8, 45.8, 44.9, 43.9, 43.0, 42.0, 41.1, 40.1, 39.2, 38.3, 37.3, 36.4, 35.5, 34.6, 33.7, 32.8, 31.9, 31.0, 30.1, 29.3, 28.4, 27.6, 26.8, 26.0, 25.2, 24.4, 23.6, 22.8, 22.1, 21.3, 20.6, 19.9, 19.2, 18.5, 17.9, 17.2, 16.6, 16.0, 15.5, 15.0, 14.5, 14.0, 13.6, 13.2],
    /*  83 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.3, 55.4, 54.4, 53.5, 52.5, 51.6, 50.6, 49.6, 48.7, 47.7, 46.8, 45.8, 44.9, 43.9, 43.0, 42.0, 41.1, 40.1, 39.2, 38.2, 37.3, 36.4, 35.4, 34.5, 33.6, 32.7, 31.8, 31.0, 30.1, 29.2, 28.4, 27.5, 26.7, 25.9, 25.1, 24.3, 23.5, 22.7, 22.0, 21.2, 20.5, 19.7, 19.0, 18.3, 17.7, 17.0, 16.4, 15.8, 15.2, 14.7, 14.2, 13.7, 13.2, 12.8, 12.4],
    /*  84 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.9, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.3, 55.4, 54.4, 53.5, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.8, 45.8, 44.9, 43.9, 42.9, 42.0, 41.0, 40.1, 39.2, 38.2, 37.3, 36.3, 35.4, 34.5, 33.6, 32.7, 31.8, 30.9, 30.0, 29.2, 28.3, 27.5, 26.7, 25.8, 25.0, 24.2, 23.4, 22.6, 21.9, 21.1, 20.4, 19.6, 18.9, 18.2, 17.5, 16.8, 16.2, 15.6, 15.0, 14.4, 13.9, 13.4, 12.9, 12.5, 12.1, 11.7],
    /*  85 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.2, 60.2, 59.2, 58.3, 57.3, 56.3, 55.4, 54.4, 53.5, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.8, 45.8, 44.8, 43.9, 42.9, 42.0, 41.0, 40.1, 39.1, 38.2, 37.3, 36.3, 35.4, 34.5, 33.6, 32.7, 31.8, 30.9, 30.0, 29.1, 28.3, 27.4, 26.6, 25.8, 25.0, 24.1, 23.3, 22.6, 21.8, 21.0, 20.3, 19.5, 18.8, 18.1, 17.4, 16.7, 16.0, 15.4, 14.8, 14.2, 13.6, 13.1, 12.6, 12.2, 11.8, 11.4, 11.0],
    /*  86 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.1, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.4, 54.4, 53.5, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 42.0, 41.0, 40.1, 39.1, 38.2, 37.2, 36.3, 35.4, 34.5, 33.5, 32.6, 31.7, 30.9, 30.0, 29.1, 28.2, 27.4, 26.6, 25.7, 24.9, 24.1, 23.3, 22.5, 21.7, 20.9, 20.2, 19.4, 18.7, 17.9, 17.2, 16.5, 15.9, 15.2, 14.6, 14.0, 13.4, 12.9, 12.4, 11.9, 11.5, 11.1, 10.7, 10.4],
    /*  87 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 70.0, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.4, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 42.0, 41.0, 40.1, 39.1, 38.2, 37.2, 36.3, 35.4, 34.4, 33.5, 32.6, 31.7, 30.8, 29.9, 29.1, 28.2, 27.4, 26.5, 25.7, 24.9, 24.0, 23.2, 22.4, 21.6, 20.9, 20.1, 19.3, 18.6, 17.8, 17.1, 16.4, 15.7, 15.1, 14.4, 13.8, 13.2, 12.7, 12.2, 11.7, 11.2, 10.8, 10.4, 10.0, 9.7],
    /*  88 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.4, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 42.0, 41.0, 40.0, 39.1, 38.2, 37.2, 36.3, 35.3, 34.4, 33.5, 32.6, 31.7, 30.8, 29.9, 29.0, 28.2, 27.3, 26.5, 25.6, 24.8, 24.0, 23.2, 22.4, 21.6, 20.8, 20.0, 19.2, 18.5, 17.7, 17.0, 16.3, 15.6, 14.9, 14.3, 13.7, 13.1, 12.5, 12.0, 11.5, 11.0, 10.5, 10.1,  9.8, 9.4, 9.1],
    /*  89 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.4, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.7, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.3, 35.3, 34.4, 33.5, 32.6, 31.7, 30.8, 29.9, 29.0, 28.2, 27.3, 26.4, 25.6, 24.8, 24.0, 23.1, 22.3, 21.5, 20.7, 20.0, 19.2, 18.4, 17.7, 16.9, 16.2, 15.5, 14.8, 14.2, 13.5, 12.9, 12.3, 11.8, 11.3, 10.8, 10.3,  9.9,  9.5, 9.1, 8.8, 8.5],
    /*  90 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.4, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.3, 35.3, 34.4, 33.5, 32.6, 31.7, 30.8, 29.9, 29.0, 28.1, 27.3, 26.4, 25.6, 24.7, 23.9, 23.1, 22.3, 21.5, 20.7, 19.9, 19.1, 18.4, 17.6, 16.9, 16.1, 15.4, 14.7, 14.1, 13.4, 12.8, 12.2, 11.6, 11.1, 10.6, 10.1,  9.7,  9.3, 8.9, 8.6, 8.3, 8.0],
    /*  91 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.9, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.4, 33.5, 32.5, 31.6, 30.7, 29.9, 29.0, 28.1, 27.3, 26.4, 25.6, 24.7, 23.9, 23.1, 22.3, 21.5, 20.7, 19.9, 19.1, 18.3, 17.5, 16.8, 16.1, 15.3, 14.6, 14.0, 13.3, 12.7, 12.1, 11.5, 10.9, 10.4,  9.9,  9.5,  9.1, 8.7, 8.3, 8.0, 7.7, 7.5],
    /*  92 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.4, 33.5, 32.5, 31.6, 30.7, 29.8, 29.0, 28.1, 27.2, 26.4, 25.5, 24.7, 23.9, 23.0, 22.2, 21.4, 20.6, 19.8, 19.0, 18.3, 17.5, 16.7, 16.0, 15.3, 14.6, 13.9, 13.2, 12.6, 11.9, 11.4, 10.8, 10.3,  9.8,  9.3,  8.9, 8.5, 8.1, 7.8, 7.5, 7.2, 7.0],
    /*  93 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.4, 33.4, 32.5, 31.6, 30.7, 29.8, 29.0, 28.1, 27.2, 26.4, 25.5, 24.7, 23.8, 23.0, 22.2, 21.4, 20.6, 19.8, 19.0, 18.2, 17.4, 16.7, 15.9, 15.2, 14.5, 13.8, 13.1, 12.5, 11.9, 11.3, 10.7, 10.1,  9.6,  9.2,  8.7, 8.3, 7.9, 7.6, 7.3, 7.0, 6.7, 6.5],
    /*  94 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.4, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.1, 27.2, 26.3, 25.5, 24.7, 23.8, 23.0, 22.2, 21.4, 20.6, 19.8, 19.0, 18.2, 17.4, 16.6, 15.9, 15.2, 14.4, 13.7, 13.1, 12.4, 11.8, 11.2, 10.6, 10.0,  9.5,  9.0,  8.6, 8.2, 7.8, 7.4, 7.1, 6.8, 6.5, 6.3, 6.1],
    /*  95 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.4, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.1, 27.2, 26.3, 25.5, 24.6, 23.8, 23.0, 22.2, 21.4, 20.6, 19.7, 18.9, 18.2, 17.4, 16.6, 15.9, 15.1, 14.4, 13.7, 13.0, 12.3, 11.7, 11.1, 10.5,  9.9,  9.4,  8.9,  8.5, 8.0, 7.6, 7.3, 6.9, 6.6, 6.4, 6.1, 5.9, 5.7],
    /*  96 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.2, 26.3, 25.5, 24.6, 23.8, 23.0, 22.2, 21.3, 20.5, 19.7, 18.9, 18.1, 17.4, 16.6, 15.8, 15.1, 14.3, 13.6, 12.9, 12.3, 11.6, 11.0, 10.4,  9.9,  9.3,  8.8,  8.4, 7.9, 7.5, 7.1, 6.8, 6.5, 6.2, 5.9, 5.7, 5.5, 5.3],
    /*  97 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.2, 26.3, 25.5, 24.6, 23.8, 23.0, 22.1, 21.3, 20.5, 19.7, 18.9, 18.1, 17.3, 16.6, 15.8, 15.0, 14.3, 13.6, 12.9, 12.2, 11.6, 11.0, 10.4,  9.8,  9.2,  8.7,  8.3, 7.8, 7.4, 7.0, 6.7, 6.4, 6.1, 5.8, 5.5, 5.3, 5.1, 4.9],
    /*  98 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.2, 26.3, 25.5, 24.6, 23.8, 22.9, 22.1, 21.3, 20.5, 19.7, 18.9, 18.1, 17.3, 16.5, 15.8, 15.0, 14.3, 13.6, 12.9, 12.2, 11.5, 10.9, 10.3,  9.7,  9.2,  8.7,  8.2, 7.7, 7.3, 6.9, 6.6, 6.2, 5.9, 5.7, 5.4, 5.2, 5.0, 4.8, 4.6],
    /*  99 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.1, 38.1, 37.2, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.2, 26.3, 25.4, 24.6, 23.8, 22.9, 22.1, 21.3, 20.5, 19.7, 18.9, 18.1, 17.3, 16.5, 15.7, 15.0, 14.3, 13.5, 12.8, 12.2, 11.5, 10.9, 10.2,  9.7,  9.1,  8.6,  8.1, 7.6, 7.2, 6.8, 6.5, 6.1, 5.8, 5.5, 5.3, 5.0, 4.8, 4.6, 4.5, 4.3],
    /* 100 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.8, 22.9, 22.1, 21.3, 20.5, 19.7, 18.9, 18.1, 17.3, 16.5, 15.7, 15.0, 14.2, 13.5, 12.8, 12.1, 11.5, 10.8, 10.2,  9.6,  9.1,  8.5,  8.0, 7.6, 7.2, 6.8, 6.4, 6.0, 5.7, 5.4, 5.2, 4.9, 4.7, 4.5, 4.3, 4.2, 4.1],
    /* 101 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.8, 22.9, 22.1, 21.3, 20.5, 19.7, 18.9, 18.1, 17.3, 16.5, 15.7, 15.0, 14.2, 13.5, 12.8, 12.1, 11.4, 10.8, 10.2,  9.6,  9.0,  8.5,  8.0, 7.5, 7.1, 6.7, 6.3, 6.0, 5.6, 5.3, 5.1, 4.8, 4.6, 4.4, 4.2, 4.1, 3.9, 3.8],
    /* 102 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.6, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.7, 18.8, 18.0, 17.3, 16.5, 15.7, 14.9, 14.2, 13.5, 12.8, 12.1, 11.4, 10.8, 10.1,  9.6,  9.0,  8.5,  8.0, 7.5, 7.0, 6.6, 6.3, 5.9, 5.6, 5.3, 5.0, 4.7, 4.5, 4.3, 4.1, 4.0, 3.8, 3.7, 3.6],
    /* 103 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.3, 16.5, 15.7, 14.9, 14.2, 13.5, 12.8, 12.1, 11.4, 10.7, 10.1,  9.5,  9.0,  8.4,  7.9, 7.4, 7.0, 6.6, 6.2, 5.9, 5.5, 5.2, 4.9, 4.7, 4.5, 4.2, 4.1, 3.9, 3.7, 3.6, 3.5, 3.4],
    /* 104 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.8, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.2, 16.5, 15.7, 14.9, 14.2, 13.5, 12.7, 12.0, 11.4, 10.7, 10.1,  9.5,  8.9,  8.4,  7.9, 7.4, 7.0, 6.6, 6.2, 5.8, 5.5, 5.2, 4.9, 4.6, 4.4, 4.2, 4.0, 3.8, 3.7, 3.5, 3.4, 3.3, 3.3],
    /* 105 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.2, 16.5, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.4, 10.7, 10.1,  9.5,  8.9,  8.4,  7.9, 7.4, 6.9, 6.5, 6.1, 5.8, 5.4, 5.1, 4.9, 4.6, 4.4, 4.1, 4.0, 3.8, 3.6, 3.5, 3.4, 3.3, 3.2, 3.1],
    /* 106 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.2, 16.5, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.4, 10.7, 10.1,  9.5,  8.9,  8.4,  7.9, 7.4, 6.9, 6.5, 6.1, 5.8, 5.4, 5.1, 4.8, 4.6, 4.3, 4.1, 3.9, 3.8, 3.6, 3.5, 3.3, 3.2, 3.2, 3.1, 3.1],
    /* 107 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.2, 16.5, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.4, 10.7, 10.1,  9.5,  8.9,  8.4,  7.9, 7.4, 6.9, 6.5, 6.1, 5.8, 5.4, 5.1, 4.8, 4.6, 4.3, 4.1, 3.9, 3.7, 3.6, 3.4, 3.3, 3.2, 3.2, 3.1, 3.1, 3.0],
    /* 108 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.5, 19.6, 18.8, 18.0, 17.2, 16.5, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.4, 10.7, 10.1,  9.5,  8.9,  8.4,  7.8, 7.4, 6.9, 6.5, 6.1, 5.7, 5.4, 5.1, 4.8, 4.5, 4.3, 4.1, 3.9, 3.7, 3.6, 3.4, 3.3, 3.2, 3.1, 3.1, 3.0, 3.0, 3.0],
    /* 109 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.3, 10.7, 10.1,  9.5,  8.9,  8.4,  7.8, 7.4, 6.9, 6.5, 6.1, 5.7, 5.4, 5.1, 4.8, 4.5, 4.3, 4.1, 3.9, 3.7, 3.6, 3.4, 3.3, 3.2, 3.1, 3.1, 3.0, 3.0, 3.0, 3.0],
    /* 110 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.3, 10.7, 10.1,  9.5,  8.9,  8.3,  7.8, 7.4, 6.9, 6.5, 6.1, 5.7, 5.4, 5.1, 4.8, 4.5, 4.3, 4.1, 3.9, 3.7, 3.5, 3.4, 3.3, 3.2, 3.1, 3.1, 3.0, 3.0, 3.0, 3.0, 3.0],
    /* 111 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.3, 10.7, 10.1,  9.5,  8.9,  8.3,  7.8, 7.3, 6.9, 6.5, 6.1, 5.7, 5.4, 5.1, 4.8, 4.5, 4.3, 4.1, 3.9, 3.7, 3.5, 3.4, 3.3, 3.2, 3.1, 3.0, 3.0, 3.0, 3.0, 3.0, 2.9, 2.9],
    /* 112 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.3, 10.7, 10.1,  9.5,  8.9,  8.3,  7.8, 7.3, 6.9, 6.5, 6.1, 5.7, 5.4, 5.1, 4.8, 4.5, 4.3, 4.0, 3.8, 3.7, 3.5, 3.4, 3.3, 3.2, 3.1, 3.0, 3.0, 3.0, 2.9, 2.9, 2.9, 2.9, 2.9],
    /* 113 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.2, 13.4, 12.7, 12.0, 11.3, 10.7, 10.0,  9.4,  8.9,  8.3,  7.8, 7.3, 6.9, 6.4, 6.1, 5.7, 5.3, 5.0, 4.7, 4.5, 4.2, 4.0, 3.8, 3.6, 3.5, 3.4, 3.2, 3.1, 3.1, 3.0, 3.0, 2.9, 2.9, 2.9, 2.9, 2.9, 2.9, 2.8],
    /* 114 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.1, 13.4, 12.7, 12.0, 11.3, 10.7, 10.0,  9.4,  8.9,  8.3,  7.8, 7.3, 6.9, 6.4, 6.0, 5.7, 5.3, 5.0, 4.7, 4.4, 4.2, 4.0, 3.8, 3.6, 3.5, 3.3, 3.2, 3.1, 3.0, 3.0, 2.9, 2.9, 2.9, 2.9, 2.9, 2.8, 2.8, 2.8, 2.8],
    /* 115 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.7, 14.9, 14.1, 13.4, 12.7, 12.0, 11.3, 10.7, 10.0,  9.4,  8.8,  8.3,  7.8, 7.3, 6.8, 6.4, 6.0, 5.6, 5.3, 5.0, 4.7, 4.4, 4.2, 4.0, 3.8, 3.6, 3.4, 3.3, 3.2, 3.1, 3.0, 2.9, 2.9, 2.9, 2.8, 2.8, 2.8, 2.8, 2.8, 2.8, 2.7, 2.7],
    /* 116 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.3, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.9, 14.1, 13.4, 12.7, 12.0, 11.3, 10.6, 10.0,  9.4,  8.8,  8.3,  7.7, 7.3, 6.8, 6.4, 6.0, 5.6, 5.2, 4.9, 4.6, 4.4, 4.1, 3.9, 3.7, 3.5, 3.3, 3.2, 3.1, 3.0, 2.9, 2.8, 2.8, 2.8, 2.8, 2.8, 2.7, 2.7, 2.7, 2.7, 2.6, 2.6, 2.5],
    /* 117 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.6, 23.7, 22.9, 22.1, 21.2, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.9, 14.1, 13.4, 12.7, 12.0, 11.3, 10.6, 10.0,  9.4,  8.8,  8.2,  7.7, 7.2, 6.8, 6.3, 5.9, 5.5, 5.2, 4.9, 4.6, 4.3, 4.0, 3.8, 3.6, 3.4, 3.3, 3.1, 3.0, 2.9, 2.8, 2.7, 2.7, 2.7, 2.7, 2.6, 2.6, 2.6, 2.6, 2.6, 2.5, 2.5, 2.4, 2.3],
    /* 118 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.3, 25.4, 24.5, 23.7, 22.9, 22.1, 21.2, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.9, 14.1, 13.4, 12.6, 11.9, 11.3, 10.6, 10.0,  9.3,  8.8,  8.2,  7.7, 7.2, 6.7, 6.3, 5.8, 5.5, 5.1, 4.8, 4.5, 4.2, 3.9, 3.7, 3.5, 3.3, 3.1, 3.0, 2.8, 2.7, 2.6, 2.6, 2.5, 2.5, 2.5, 2.5, 2.5, 2.4, 2.4, 2.4, 2.4, 2.3, 2.2, 2.1, 1.9],
    /* 119 */[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.7, 29.8, 28.9, 28.0, 27.1, 26.2, 25.4, 24.5, 23.7, 22.9, 22.1, 21.2, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.8, 14.1, 13.4, 12.6, 11.9, 11.2, 10.6,  9.9,  9.3,  8.7,  8.2,  7.6, 7.1, 6.6, 6.2, 5.8, 5.4, 5.0, 4.7, 4.4, 4.1, 3.8, 3.6, 3.3, 3.1, 2.9, 2.8, 2.6, 2.5, 2.4, 2.4, 2.3, 2.3, 2.3, 2.3, 2.2, 2.2, 2.2, 2.2, 2.1, 2.1, 2.0, 1.9, 1.7, 1.3],
    /* 120+*/[84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.6, 29.8, 28.9, 28.0, 27.1, 26.2, 25.4, 24.5, 23.7, 22.9, 22.0, 21.2, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.8, 14.1, 13.3, 12.6, 11.9, 11.2, 10.5,  9.9,  9.3,  8.7,  8.1,  7.6, 7.1, 6.6, 6.1, 5.7, 5.3, 4.9, 4.6, 4.3, 4.0, 3.7, 3.4, 3.2, 3.0, 2.8, 2.6, 2.5, 2.3, 2.2, 2.1, 2.1, 2.1, 2.0, 2.0, 2.0, 2.0, 2.0, 1.9, 1.9, 1.8, 1.8, 1.6, 1.4, 1.1, 1.0]
    //     age:  0,    1,    2,    3,    4,    5,    6,    7,    8,    9,   10,   11,   12,   13,   14,   15,   16,   17,   18,   19,   20,   21,   22,   23,   24,   25,   26,   27,   28,   29,   30,   31,   32,   33,   34,   35,   36,   37,   38,   39,   40,   41,   42,   43,   44,   45,   46,   47,   48,   49,   50,   51,   52,   53,   54,   55,   56,   57,   58,   59,   60,   61,   62,   63,   64,   65,   66,   67,   68,   69,   70,   71,   72,   73,   74,   75,   76,   77,   78,   79,   80,   81,   82,   83,   84,   85,   86,  87,  88,  89,  90,  91,  92,  93,  94,  95,  96,  97,  98,  99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120+
]; /* typo corrections:
	- The source table/matrix was symmetric, except for [2][5] = 88.5 and [5][2] = 88.6. The smaller of the two (88.5) was chosen.
	- [19][35] is blank on source table, so we set it equal to [35][19] = (68.0).
*/

// Single Life Expectancy Table (Table I) (https://www.irahelp.com/printable/2022-single-life-expectancy-table)
//               age: 0,    1,    2,    3,    4,    5,    6,    7,    8,    9,   10,   11,   12,   13,   14,   15,   16,   17,   18,   19,   20,   21,   22,   23,   24,   25,   26,   27,   28,   29,   30,   31,   32,   33,   34,   35,   36,   37,   38,   39,   40,   41,   42,   43,   44,   45,   46,   47,   48,   49,   50,   51,   52,   53,   54,   55,   56,   57,   58,   59,   60,   61,   62,   63,   64,   65,   66,   67,   68,   69,   70,   71,   72,   73,   74,   75,   76,   77,   78,   79,   80,   81,  82,  83,  84,  85,  86,  87,  88,  89,  90,  91,  92,  93,  94,  95,  96,  97,  98,  99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120+
const TABLE1_1D = [84.6, 83.7, 82.8, 81.8, 80.8, 79.8, 78.8, 77.9, 76.9, 75.9, 74.9, 73.9, 72.9, 71.9, 70.9, 69.9, 69.0, 68.0, 67.0, 66.0, 65.0, 64.1, 63.1, 62.1, 61.1, 60.2, 59.2, 58.2, 57.3, 56.3, 55.3, 54.4, 53.4, 52.5, 51.5, 50.5, 49.6, 48.6, 47.7, 46.7, 45.7, 44.8, 43.8, 42.9, 41.9, 41.0, 40.0, 39.0, 38.1, 37.1, 36.2, 35.3, 34.3, 33.4, 32.5, 31.6, 30.6, 29.8, 28.9, 28.0, 27.1, 26.2, 25.4, 24.5, 23.7, 22.9, 22.0, 21.2, 20.4, 19.6, 18.8, 18.0, 17.2, 16.4, 15.6, 14.8, 14.1, 13.3, 12.6, 11.9, 11.2, 10.5, 9.9, 9.3, 8.7, 8.1, 7.6, 7.1, 6.6, 6.1, 5.7, 5.3, 4.9, 4.6, 4.3, 4.0, 3.7, 3.4, 3.2, 3.0, 2.8, 2.6, 2.5, 2.3, 2.2, 2.1, 2.1, 2.1, 2.0, 2.0, 2.0, 2.0, 2.0, 1.9, 1.9, 1.8, 1.8, 1.6, 1.4, 1.1, 1.0];

export default Calculator